import { useState, useEffect, useMemo, useCallback } from 'react';
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useEmpresa } from '../contexts/EmpresaContext';
import { organizacaoService } from '../services/organizacao.service';
import { formatCNPJ, formatCPF } from '../utils/formatters';
import { useToast, ToastContainer } from '../components/Toast';
import * as XLSX from 'xlsx';
import ColaboradorFichaAccordion from '../components/Organizacao/ColaboradorFichaAccordion';
import { colaboradoresService, type Colaborador } from '../services/colaboradores.service';
import { cargosService } from '../services/cargos.service';
import { setoresService } from '../services/setores.service';
import { useAuth } from '../contexts/AuthContext';
import { ModalAdmissaoRetroativa } from '../components/Colaboradores/ModalAdmissaoRetroativa';
import { ModalDesligamentoSemDemissional } from '../components/Colaboradores/ModalDesligamentoSemDemissional';
import TransferirColaboradoresModal from '../components/Colaboradores/TransferirColaboradoresModal';
import ConfirmDialog from '../components/ConfirmDialog';
import { ModalCriarCargoSetor } from '../components/Organizacao/ModalCriarCargoSetor';
import { ModalCriarSetor } from '../components/Organizacao/ModalCriarSetor';
import { ModalMudancaFuncao } from '../components/Organizacao/ModalMudancaFuncao';
import Toggle from '../components/Toggle';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Users,
  TrendingUp,
  TrendingDown,
  UserPlus,
  UserMinus,
  AlertTriangle,
  Search,
  Building2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  GitBranch,
  ShieldCheck,
  Stethoscope,
  X,
  Edit3,
  Layers,
  RefreshCw,
  Clock,
  Briefcase,
  FileDown,
  Info,
  ArrowUpRight,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Award,
  Plus,
  ArrowRightLeft,
} from 'lucide-react';
import type {
  OrganizacaoKpisContract,
  OrganizacaoOrganogramaContract,
  OrganizacaoCargoHierarquiaContract,
  OrganizacaoPessoasItemContract,
  OrganizacaoColaboradorItemContract,
  OrganizacaoMovimentacaoItemContract,
  OrganizacaoCargoDetalhesContract,
} from '@sstexames/shared-types';

export default function OrganizacaoPage() {
  const { empresa, isAdmin } = useEmpresa();
  const { user } = useAuth();
  const toast = useToast();

  // Autosserviço de Quadro: Modais e Listas
  const [isRetroModalOpen, setIsRetroModalOpen] = useState(false);
  const [isDesligamentoModalOpen, setIsDesligamentoModalOpen] = useState(false);
  const [desligamentoTargetColaborador, setDesligamentoTargetColaborador] = useState<Colaborador | null>(null);
  const [cargosList, setCargosList] = useState<Array<{ id: number; nome: string }>>([]);
  const [setoresList, setSetoresList] = useState<Array<{ id: number; nome: string }>>([]);

  // Modal Criar Cargo no Setor (com IA e Cargo Semelhante)
  const [isCriarCargoModalOpen, setIsCriarCargoModalOpen] = useState(false);
  const [criarCargoTargetSetor, setCriarCargoTargetSetor] = useState<{ setor_id: number; setor_nome: string } | null>(null);

  const handleOpenCriarCargo = (setor: { setor_id: number; setor_nome: string }) => {
    setCriarCargoTargetSetor(setor);
    setIsCriarCargoModalOpen(true);
  };

  const handleCargoCreated = () => {
    loadTabData();
    loadKpis();
    if (empresa?.id) {
      cargosService.findAll(1000).then((res) => setCargosList(res.data || [])).catch(() => {});
    }
  };

  // Modal Criar Setor
  const [isCriarSetorModalOpen, setIsCriarSetorModalOpen] = useState(false);

  const handleSetorCreated = () => {
    loadTabData();
    loadKpis();
    if (empresa?.id) {
      setoresService.findAll(1000).then((res) => setSetoresList(res.data || [])).catch(() => {});
    }
  };

  // Modal Mudança de Função (com validação de riscos/aptidões da NR-7)
  const [isMudancaFuncaoModalOpen, setIsMudancaFuncaoModalOpen] = useState(false);
  const [mudancaFuncaoTargetColaborador, setMudancaFuncaoTargetColaborador] = useState<OrganizacaoColaboradorItemContract | null>(null);

  const [highlightedColaboradorId, setHighlightedColaboradorId] = useState<number | null>(null);

  // Modal Transferir Colaboradores (Remanejamento entre filiais do mesmo Grupo)
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [isConfirmTransferAllOpen, setIsConfirmTransferAllOpen] = useState(false);
  const [transferTargetColaboradores, setTransferTargetColaboradores] = useState<Colaborador[]>([]);

  // Consulta de Filiais do Grupo Econômico para habilitar/desabilitar transferências
  const { data: filiaisData } = useQuery({
    queryKey: ['colaboradores-filiais', empresa?.id],
    queryFn: () => colaboradoresService.getFiliais(),
    enabled: !!empresa?.id,
  });
  const filiaisDisponiveis = filiaisData?.filiais || [];
  const hasGrupoEconomicoFiliais = filiaisDisponiveis.length > 0;

  const handleOpenTransferir = (colab?: OrganizacaoColaboradorItemContract | null) => {
    if (colab) {
      setTransferTargetColaboradores([colab as unknown as Colaborador]);
    } else {
      setTransferTargetColaboradores(colaboradoresFiltrados as unknown as Colaborador[]);
    }
    setIsTransferModalOpen(true);
  };

  const handleOpenMudancaFuncao = (colab: OrganizacaoColaboradorItemContract) => {
    setMudancaFuncaoTargetColaborador(colab);
    setIsMudancaFuncaoModalOpen(true);
  };

  const handleMudancaFuncaoSuccess = (colaboradorId?: number) => {
    loadTabData();
    loadKpis();
    if (colaboradorId) {
      setHighlightedColaboradorId(colaboradorId);
      setTimeout(() => {
        const el =
          document.getElementById(`colab-row-${colaboradorId}`) ||
          document.getElementById(`colab-card-${colaboradorId}`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 350);
    }
  };

  useEffect(() => {
    if (!empresa?.id) return;
    cargosService.findAll(1000).then((res) => setCargosList(res.data || [])).catch(() => {});
    setoresService.findAll(1000).then((res) => setSetoresList(res.data || [])).catch(() => {});
  }, [empresa?.id]);

  const handleOpenDesligamento = (colab?: Colaborador | null) => {
    setDesligamentoTargetColaborador(colab || null);
    setIsDesligamentoModalOpen(true);
  };

  // Active Tab: 'organograma' | 'pessoas' | 'movimentacoes'
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromQuery = searchParams.get('tab') as 'organograma' | 'pessoas' | 'movimentacoes' | null;
  const [activeTab, setActiveTab] = useState<'organograma' | 'pessoas' | 'movimentacoes'>(
    tabFromQuery && ['organograma', 'pessoas', 'movimentacoes'].includes(tabFromQuery)
      ? tabFromQuery
      : 'organograma',
  );

  const handleTabChange = useCallback(
    (tab: 'organograma' | 'pessoas' | 'movimentacoes') => {
      setActiveTab(tab);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('tab', tab);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  useEffect(() => {
    const tabUrl = searchParams.get('tab') as 'organograma' | 'pessoas' | 'movimentacoes' | null;
    if (tabUrl && ['organograma', 'pessoas', 'movimentacoes'].includes(tabUrl) && tabUrl !== activeTab) {
      setActiveTab(tabUrl);
    }
  }, [searchParams, activeTab]);

  // Loading and error states
  const [loadingKpis, setLoadingKpis] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Colaborador em edição (accordion inline no organograma)
  const [editingColaboradorId, setEditingColaboradorId] = useState<number | null>(null);

  // Sorting state for table
  const [sortField, setSortField] = useState<'nome' | 'setor_nome' | 'cargo_nome' | 'data_admissao'>('nome');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [showStatusSstTooltip, setShowStatusSstTooltip] = useState(false);

  const handleSort = (field: 'nome' | 'setor_nome' | 'cargo_nome' | 'data_admissao') => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Data states
  const [kpis, setKpis] = useState<OrganizacaoKpisContract | null>(null);
  const [organograma, setOrganograma] = useState<OrganizacaoOrganogramaContract | null>(null);
  const [pessoas, setPessoas] = useState<OrganizacaoPessoasItemContract[]>([]);
  const [movimentacoes, setMovimentacoes] = useState<OrganizacaoMovimentacaoItemContract[]>([]);

  // Filtering states
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [movPeriodo, setMovPeriodo] = useState<'hoje' | 'semana' | 'mes' | 'ano' | 'tudo'>('mes');
  const [selectedSetorFilter, setSelectedSetorFilter] = useState<number | 'all'>('all');
  const [apenasComOcupantes, setApenasComOcupantes] = useState(false);
  const [pessoasStatusFilter, setPessoasStatusFilter] = useState<'all' | 'ativo' | 'candidato' | 'desligado'>('all');

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 350);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  // Tree UI state
  const [expandedSetores, setExpandedSetores] = useState<Record<number, boolean>>({});
  const [expandedOccupants, setExpandedOccupants] = useState<Record<number, boolean>>({});

  // Drawer state (Cargo details)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedCargoId, setSelectedCargoId] = useState<number | null>(null);
  const [cargoDetalhes, setCargoDetalhes] = useState<OrganizacaoCargoDetalhesContract | null>(null);
  const [loadingDrawer, setLoadingDrawer] = useState(false);

  // Modal state (Definir superior)
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalTargetCargo, setModalTargetCargo] = useState<{ id: number; nome: string; setor_id?: number } | null>(null);
  const [modalSuperiorId, setModalSuperiorId] = useState<number | ''>('');
  const [modalNivel, setModalNivel] = useState<string>('Supervisão');
  const [savingSuperior, setSavingSuperior] = useState(false);

  // Estado de download de ASO do colaborador
  const [downloadingColabAsoId, setDownloadingColabAsoId] = useState<number | null>(null);

  const handleDownloadColaboradorAso = async (colaboradorId: number, nomeColaborador: string) => {
    try {
      setDownloadingColabAsoId(colaboradorId);
      const blob = await colaboradoresService.downloadColaboradorAso(colaboradorId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cleanName = (nomeColaborador || 'colaborador').replace(/[^a-zA-Z0-9]/g, '_');
      a.download = `ASO_${cleanName}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Download do ASO concluído com sucesso!');
    } catch (err: unknown) {
      console.error('Erro ao baixar ASO do colaborador:', err);
      toast.error('Não foi possível realizar o download do ASO no momento.');
    } finally {
      setDownloadingColabAsoId(null);
    }
  };

  // Carregar KPIs
  const loadKpis = useCallback(async () => {
    if (!empresa?.id) return;
    try {
      setLoadingKpis(true);
      const data = await organizacaoService.getKpis(empresa.id);
      setKpis(data);
    } catch (err: unknown) {
      console.error('Erro ao carregar KPIs:', err);
    } finally {
      setLoadingKpis(false);
    }
  }, [empresa?.id]);

  const movimentacoesSearchFilter = activeTab === 'movimentacoes' ? debouncedSearchQuery : '';

  // Carregar dados de acordo com a aba ativa
  const loadTabData = useCallback(async () => {
    if (!empresa?.id) return;
    try {
      setLoadingData(true);
      setErrorMessage(null);

      if (activeTab === 'organograma') {
        const data = await organizacaoService.getOrganograma(empresa.id);
        setOrganograma(data);
        // Expandir por padrão todos os setores
        const initialExpanded: Record<number, boolean> = {};
        for (const s of data.setores) {
          initialExpanded[s.setor_id] = true;
        }
        setExpandedSetores(initialExpanded);
      } else if (activeTab === 'pessoas') {
        const data = await organizacaoService.getPessoas(empresa.id);
        setPessoas(data);
      } else if (activeTab === 'movimentacoes') {
        const data = await organizacaoService.getMovimentacoes(
          empresa.id,
          movPeriodo,
          movimentacoesSearchFilter || undefined,
          selectedSetorFilter === 'all' ? undefined : selectedSetorFilter,
        );
        setMovimentacoes(data);
      }
    } catch (err: unknown) {
      console.error('Erro ao carregar dados da aba:', err);
      setErrorMessage('Não foi possível carregar os dados organizacionais.');
    } finally {
      setLoadingData(false);
    }
  }, [
    empresa?.id,
    activeTab,
    movPeriodo,
    selectedSetorFilter,
    movimentacoesSearchFilter,
  ]);

  useEffect(() => {
    loadKpis();
  }, [loadKpis]);

  useEffect(() => {
    loadTabData();
  }, [loadTabData]);

  // Callback de sucesso ao salvar edição do colaborador
  const handleColaboradorSaved = useCallback(() => {
    setEditingColaboradorId(null);
    toast.success('Cadastro do colaborador atualizado com sucesso!');
    loadTabData();
    loadKpis();
  }, [loadTabData, loadKpis, toast]);

  // Lista plana de todos os colaboradores da aba Pessoas
  const todosColaboradoresPessoas = useMemo(() => {
    const list: OrganizacaoColaboradorItemContract[] = [];
    const seenIds = new Set<number>();

    for (const p of pessoas) {
      for (const c of p.colaboradores || []) {
        if (!seenIds.has(c.id)) {
          seenIds.add(c.id);
          list.push(c);
        }
      }
    }
    return list;
  }, [pessoas]);

  // Contadores por status para os botões de filtro
  const statusCounts = useMemo(() => {
    let all = 0;
    let ativo = 0;
    let candidato = 0;
    let desligado = 0;

    for (const c of todosColaboradoresPessoas) {
      if (selectedSetorFilter !== 'all' && c.setor_id !== selectedSetorFilter) continue;
      all++;
      if (c.status === 'ativo') ativo++;
      else if (c.status === 'candidato') candidato++;
      else if (c.status === 'desligado') desligado++;
    }

    return { all, ativo, candidato, desligado };
  }, [todosColaboradoresPessoas, selectedSetorFilter]);

  // Lista direta de colaboradores filtrados para a aba Pessoas
  const colaboradoresFiltrados = useMemo(() => {
    const cleanSearchText = searchQuery.trim().toLowerCase();
    const cleanSearchNorm = cleanSearchText.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const cleanSearchDigits = searchQuery.replace(/\D/g, '');

    const filtered = todosColaboradoresPessoas.filter((colab) => {
      // Filtro de Setor
      if (selectedSetorFilter !== 'all' && colab.setor_id !== selectedSetorFilter) {
        return false;
      }

      // Filtro de Status
      if (pessoasStatusFilter !== 'all' && colab.status !== pessoasStatusFilter) {
        return false;
      }

      // Se não há texto de busca, retorna todos do setor/status
      if (!cleanSearchText) return true;

      const colabNome = (colab.nome || '').toLowerCase();
      const colabNomeNorm = colabNome.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      const colabNomeSocial = (colab.nome_social || '').toLowerCase();
      const colabNomeSocialNorm = colabNomeSocial.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      const colabCpfDigits = (colab.cpf || '').replace(/\D/g, '');
      const colabCpfFormatted = formatCPF(colab.cpf || '').toLowerCase();
      const colabCargo = (colab.cargo_nome || '').toLowerCase();
      const colabCargoNorm = colabCargo.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      const colabSetor = (colab.setor_nome || '').toLowerCase();
      const colabSetorNorm = colabSetor.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      const colabMatricula = (colab.matricula || '').toLowerCase();

      const matchNome =
        colabNome.includes(cleanSearchText) ||
        colabNomeNorm.includes(cleanSearchNorm) ||
        colabNomeSocial.includes(cleanSearchText) ||
        colabNomeSocialNorm.includes(cleanSearchNorm);
      const matchCpfDigits = cleanSearchDigits.length > 0 && colabCpfDigits.includes(cleanSearchDigits);
      const matchCpfFormatted = colabCpfFormatted.includes(cleanSearchText);
      const matchCargo = colabCargo.includes(cleanSearchText) || colabCargoNorm.includes(cleanSearchNorm);
      const matchSetor = colabSetor.includes(cleanSearchText) || colabSetorNorm.includes(cleanSearchNorm);
      const matchMatricula = colabMatricula.includes(cleanSearchText);

      return matchNome || matchCpfDigits || matchCpfFormatted || matchCargo || matchSetor || matchMatricula;
    });

    return filtered.sort((a, b) => {
      if (sortField === 'data_admissao') {
        const timeA = a.data_admissao ? new Date(a.data_admissao + 'T12:00:00').getTime() : 0;
        const timeB = b.data_admissao ? new Date(b.data_admissao + 'T12:00:00').getTime() : 0;
        return sortDirection === 'asc' ? timeA - timeB : timeB - timeA;
      }

      const valA = (a[sortField] || '').toString();
      const valB = (b[sortField] || '').toString();
      const comp = valA.localeCompare(valB, 'pt-BR', { sensitivity: 'base' });
      return sortDirection === 'asc' ? comp : -comp;
    });
  }, [todosColaboradoresPessoas, searchQuery, selectedSetorFilter, sortField, sortDirection, pessoasStatusFilter]);

  // Paginação para a aba Pessoas
  const [pessoasPage, setPessoasPage] = useState(1);
  const [pessoasPageSize, setPessoasPageSize] = useState(20);

  useEffect(() => {
    setPessoasPage(1);
  }, [searchQuery, selectedSetorFilter, sortField, sortDirection, pessoasStatusFilter]);

  const totalPessoas = colaboradoresFiltrados.length;
  const totalPagesPessoas = Math.max(1, Math.ceil(totalPessoas / pessoasPageSize));
  const colaboradoresPaginados = useMemo(() => {
    const startIndex = (pessoasPage - 1) * pessoasPageSize;
    return colaboradoresFiltrados.slice(startIndex, startIndex + pessoasPageSize);
  }, [colaboradoresFiltrados, pessoasPage, pessoasPageSize]);

  // Setores filtrados no Organograma (busca, setor específico e toggle apenas com ocupantes)
  const setoresFiltrados = useMemo(() => {
    if (!organograma?.setores) return [];
    return organograma.setores.filter((setor) => {
      if (selectedSetorFilter !== 'all' && setor.setor_id !== selectedSetorFilter) {
        return false;
      }
      if (apenasComOcupantes && (setor.total_colaboradores || 0) === 0) {
        return false;
      }
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchSetor = (setor.setor_nome || '').toLowerCase().includes(q);
        const matchCargos = setor.cargos_raiz?.some(
          (c) =>
            (c.cargo_nome || '').toLowerCase().includes(q) ||
            c.colaboradores?.some(
              (col) =>
                (col.nome || '').toLowerCase().includes(q) ||
                (col.cpf || '').includes(q),
            ),
        );
        if (!matchSetor && !matchCargos) return false;
      }
      return true;
    });
  }, [organograma?.setores, selectedSetorFilter, apenasComOcupantes, searchQuery]);

  // Linha do tempo de movimentações sempre ordenada pela data mais recente (decrescente)
  const sortedMovimentacoes = useMemo(() => {
    return [...movimentacoes].sort((a, b) => {
      const getSafeTimestamp = (dateVal: string | Date | null | undefined): number => {
        if (!dateVal) return 0;
        if (dateVal instanceof Date) {
          const t = dateVal.getTime();
          return isNaN(t) ? 0 : t;
        }
        const str = String(dateVal).trim();
        if (/^\d{2}\/\d{2}\/\d{4}/.test(str)) {
          const [d, m, y] = str.split('/');
          const parsed = new Date(Number(y), Number(m) - 1, Number(d)).getTime();
          return isNaN(parsed) ? 0 : parsed;
        }
        const parsed = new Date(str).getTime();
        return isNaN(parsed) ? 0 : parsed;
      };

      const timeA = getSafeTimestamp(a.data_evento);
      const timeB = getSafeTimestamp(b.data_evento);
      if (timeB !== timeA) return timeB - timeA;
      return (b.colaborador_id || 0) - (a.colaborador_id || 0);
    });
  }, [movimentacoes]);

  // Exportar dados da aba Pessoas para Excel (.xlsx)
  const handleExportColaboradoresExcel = () => {
    if (!colaboradoresFiltrados || colaboradoresFiltrados.length === 0) {
      toast.error('Nenhum colaborador disponível para exportação.');
      return;
    }

    try {
      const getStatusLabel = (status?: string) => {
        switch (status) {
          case 'em_dia':
            return 'Em dia';
          case 'a_vencer':
            return 'A Vencer';
          case 'vencido':
            return 'Vencido';
          case 'agendado':
            return 'Agendado';
          case 'sem_data':
          default:
            return 'Sem Data';
        }
      };

      const rows = colaboradoresFiltrados.map((c) => ({
        'Nome': c.nome || '',
        'Nome Social': c.nome_social || '',
        'CPF': c.cpf ? formatCPF(c.cpf) : '',
        'PCD': c.pcd ? 'Sim' : 'Não',
        'Enviar ao eSocial': c.enviar_esocial !== false ? 'Sim' : 'Não',
        'Matrícula': c.matricula || '',
        'Setor': c.setor_nome || '',
        'Cargo': c.cargo_nome || '',
        'Data de Admissão': c.data_admissao || '',
        'Possui ASO': c.has_aso ? 'Sim' : 'Não informado',
        'Situação Cadastral': c.status === 'ativo' ? 'Ativo' : c.status === 'candidato' ? 'Candidato' : 'Desligado',
        'Status Periódico': getStatusLabel(c.status_periodico),
      }));

      const ws = XLSX.utils.json_to_sheet(rows);
      ws['!cols'] = [
        { wch: 32 }, // Nome
        { wch: 32 }, // Nome Social
        { wch: 16 }, // CPF
        { wch: 10 }, // PCD
        { wch: 18 }, // Enviar ao eSocial
        { wch: 15 }, // Matrícula
        { wch: 25 }, // Setor
        { wch: 25 }, // Cargo
        { wch: 18 }, // Admissão
        { wch: 14 }, // Possui ASO
        { wch: 18 }, // Situação Cadastral
        { wch: 18 }, // Status Periódico
      ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Colaboradores');

      const cleanEmpresa = (empresa?.nome || 'empresa').replace(/[^a-zA-Z0-9]/g, '_');
      const filename = `colaboradores_${cleanEmpresa}_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, filename);
      toast.success('Download da planilha de colaboradores concluído com sucesso!');
    } catch (err) {
      console.error('Erro ao exportar planilha de colaboradores:', err);
      toast.error('Não foi possível gerar o arquivo Excel no momento.');
    }
  };

  // Abrir Drawer com detalhes do cargo
  const handleOpenCargoDetalhes = async (
    cargoId: number,
    setorId?: number,
    setorNome?: string,
    cargoNome?: string,
    cbo?: string | null,
  ) => {
    if (!empresa?.id) return;
    setSelectedCargoId(cargoId);
    setIsDrawerOpen(true);
    setLoadingDrawer(true);
    // Pre-popula os dados básicos do cargo clicado imediatamente para evitar campos vazios enquanto carrega
    setCargoDetalhes({
      cargo_id: cargoId,
      cargo_nome: cargoNome || 'Detalhes do Cargo',
      cbo: cbo || null,
      descricao: null,
      setor_id: setorId || 0,
      setor_nome: setorNome || '',
      superior_cargo_id: null,
      superior_cargo_nome: null,
      nivel: null,
      subordinados: [],
      total_colaboradores: 0,
      colaboradores: [],
      riscos: [],
      exames: [],
    });
    try {
      const details = await organizacaoService.getCargoDetalhes(cargoId, empresa.id, setorId);
      setCargoDetalhes(details);
    } catch (err) {
      console.error('Erro ao carregar detalhes do cargo:', err);
      toast.error('Não foi possível carregar os detalhes de SST deste cargo.');
    } finally {
      setLoadingDrawer(false);
    }
  };

  // Abrir Modal de Definir Superior
  const handleOpenDefinirSuperior = (cargoId: number, cargoNome: string, setorId?: number, superiorId?: number | null, nivel?: string | null) => {
    setModalTargetCargo({ id: cargoId, nome: cargoNome, setor_id: setorId });
    setModalSuperiorId(superiorId || '');
    setModalNivel(nivel || 'Supervisão');
    setIsModalOpen(true);
  };

  // Salvar Hierarquia
  const handleSalvarSuperior = async () => {
    if (!empresa?.id || !modalTargetCargo) return;
    try {
      setSavingSuperior(true);
      await organizacaoService.salvarCargoSuperior({
        empresa_id: empresa.id,
        cargo_id: modalTargetCargo.id,
        superior_cargo_id: modalSuperiorId ? Number(modalSuperiorId) : null,
        setor_id: modalTargetCargo.setor_id,
        nivel: modalNivel,
      });
      setIsModalOpen(false);
      // Recarregar dados e atualizar drawer se estiver aberto
      loadTabData();
      if (selectedCargoId === modalTargetCargo.id) {
        handleOpenCargoDetalhes(modalTargetCargo.id, modalTargetCargo.setor_id);
      }
    } catch (err: unknown) {
      const errorMsg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Erro ao salvar hierarquia do cargo.';
      alert(errorMsg);
    } finally {
      setSavingSuperior(false);
    }
  };

  // Lista plana de todos os cargos disponíveis na empresa para o modal
  const availableCargos = useMemo(() => {
    if (!organograma) return [];
    const list: Array<{ id: number; nome: string; setor_nome: string }> = [];
    for (const s of organograma.setores) {
      const traverse = (nodes: OrganizacaoCargoHierarquiaContract[]) => {
        for (const n of nodes) {
          list.push({ id: n.cargo_id, nome: n.cargo_nome, setor_nome: s.setor_nome });
          if (n.subordinados && n.subordinados.length > 0) {
            traverse(n.subordinados);
          }
        }
      };
      traverse(s.cargos_raiz);
    }
    return list;
  }, [organograma]);

  // Alternar expansão de setor
  const toggleSetor = (setorId: number) => {
    setExpandedSetores((prev) => ({ ...prev, [setorId]: !prev[setorId] }));
  };

  // Alternar expansão de ocupantes
  const toggleOccupants = (cargoId: number) => {
    setExpandedOccupants((prev) => ({ ...prev, [cargoId]: !prev[prev[cargoId] ? 0 : cargoId] ? !prev[cargoId] : false }));
  };

  return (
    <div className="space-y-6 pb-12">
      {/* Top Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b border-gray-200 pb-5">
        <div>
          <div className="flex items-center space-x-2 text-xs font-semibold uppercase tracking-wider text-primary-700">
            <Building2 className="w-4 h-4" />
            <span>Gestão Estrutural & Pessoas</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mt-1 flex items-center gap-2">
            <span>Organização</span>
            {kpis?.razao_social && (
              <span className="text-sm font-normal text-gray-500">
                · {kpis.razao_social}
              </span>
            )}
          </h1>
          <p className="text-sm text-gray-600 mt-0.5">
            Visualize o organograma funcional, alocação de cargos, conformidade SST e movimentações.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2.5 sm:gap-3 w-full sm:w-auto">
          <div className="relative flex-1 sm:flex-none">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" />
            <input
              type="text"
              placeholder="Buscar colaborador, cargo..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent w-full sm:w-64 shadow-sm"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2.5 top-2.5 text-gray-400 hover:text-gray-600"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {organograma?.setores && organograma.setores.length > 0 && (
            <select
              value={selectedSetorFilter}
              onChange={(e) => setSelectedSetorFilter(e.target.value === 'all' ? 'all' : Number(e.target.value))}
              className="px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent shadow-sm text-gray-700"
            >
              <option value="all">Todos os Setores</option>
              {organograma.setores.map((s) => (
                <option key={s.setor_id} value={s.setor_id}>
                  {s.setor_nome}
                </option>
              ))}
            </select>
          )}

          <button
            onClick={() => {
              loadKpis();
              loadTabData();
            }}
            disabled={loadingData || loadingKpis}
            title="Atualizar dados"
            className="p-2 border border-gray-300 bg-white rounded-lg hover:bg-gray-50 text-gray-600 hover:text-gray-900 transition-colors shadow-sm disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${(loadingData || loadingKpis) ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Error Message Banner */}
      {errorMessage && (
        <div className="bg-rose-50 border border-rose-200 text-rose-800 px-4 py-3 rounded-xl text-sm flex items-center justify-between">
          <span>{errorMessage}</span>
          <button onClick={() => setErrorMessage(null)} className="text-rose-500 hover:text-rose-700">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* 5 Non-Duplicative KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3.5">
        {/* KPI 1: Ativos Hoje */}
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-2xs hover:border-gray-300 transition-all flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Ativos Hoje</span>
            <div className="w-8 h-8 rounded-lg bg-primary-50 text-primary-600 border border-primary-100 flex items-center justify-center">
              <Users className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-2.5 flex items-baseline gap-1.5">
            <span className="text-2xl font-bold text-gray-900">
              {loadingKpis ? '...' : (kpis?.ativos_hoje ?? 0).toLocaleString('pt-BR')}
            </span>
            <span className="text-xs text-gray-500">headcount ativo</span>
          </div>
          <div className="mt-2.5">
            <span className="text-[11px] text-primary-700 bg-primary-50 border border-primary-100 rounded px-2 py-0.5 inline-block font-medium">
              Folha vigente
            </span>
          </div>
        </div>

        {/* KPI 2: Admissões no Mês */}
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-2xs hover:border-gray-300 transition-all flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Admissões</span>
            <div className="w-8 h-8 rounded-lg bg-primary-50 text-primary-600 border border-primary-100 flex items-center justify-center">
              <TrendingUp className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-2.5 flex items-baseline gap-1.5">
            <span className="text-2xl font-bold text-primary-700">
              {loadingKpis
                ? '...'
                : (kpis?.admissoes_mes ?? 0) > 0
                ? `+${kpis?.admissoes_mes}`
                : '0'}
            </span>
            <span className="text-xs text-gray-500">no mês</span>
          </div>
          <div className="mt-2.5">
            <span className="text-[11px] text-gray-600 bg-gray-50 border border-gray-200 rounded px-2 py-0.5 inline-block font-medium">
              Entradas recentes
            </span>
          </div>
        </div>

        {/* KPI 3: Candidatos em Processo */}
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-2xs hover:border-gray-300 transition-all flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Candidatos</span>
            <div className="w-8 h-8 rounded-lg bg-gray-50 text-gray-600 border border-gray-200 flex items-center justify-center">
              <UserPlus className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-2.5 flex items-baseline gap-1.5">
            <span className="text-2xl font-bold text-gray-900">
              {loadingKpis ? '...' : (kpis?.candidatos_mes ?? 0).toLocaleString('pt-BR')}
            </span>
            <span className="text-xs text-gray-500">em processo</span>
          </div>
          <div className="mt-2.5">
            <span className="text-[11px] text-gray-600 bg-gray-50 border border-gray-200 rounded px-2 py-0.5 inline-block font-medium">
              Admissional pendente
            </span>
          </div>
        </div>

        {/* KPI 4: Desligamentos no Mês */}
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-2xs hover:border-gray-300 transition-all flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Desligamentos</span>
            <div className="w-8 h-8 rounded-lg bg-gray-50 text-gray-500 border border-gray-200 flex items-center justify-center">
              <TrendingDown className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-2.5 flex items-baseline gap-1.5">
            <span className="text-2xl font-bold text-gray-900">
              {loadingKpis
                ? '...'
                : (kpis?.desligamentos_mes ?? 0) > 0
                ? `-${kpis?.desligamentos_mes}`
                : '0'}
            </span>
            <span className="text-xs text-gray-500">no mês</span>
          </div>
          <div className="mt-2.5">
            <span className="text-[11px] text-gray-600 bg-gray-50 border border-gray-200 rounded px-2 py-0.5 inline-block font-medium">
              Saídas no período
            </span>
          </div>
        </div>

        {/* KPI 5: Pendências ASO com link direto para Vencimento de Exames */}
        <Link
          to="/vencimentos-exames?tab=sem_data"
          title="Clique para gerenciar e regularizar datas de exames em Vencimentos de Exames"
          className="group bg-white rounded-xl p-4 border border-amber-200 hover:border-amber-400 hover:shadow-md transition-all flex flex-col justify-between cursor-pointer"
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider group-hover:text-amber-800 transition-colors">
              Pendências ASO
            </span>
            <div className="w-8 h-8 rounded-lg bg-amber-50 text-amber-600 border border-amber-200 group-hover:bg-amber-100 group-hover:text-amber-700 flex items-center justify-center transition-colors">
              <AlertTriangle className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-2.5 flex items-baseline gap-1.5">
            <span className={`text-2xl font-bold ${(kpis?.pendencias_aso ?? 0) > 0 ? 'text-amber-700' : 'text-gray-900'}`}>
              {loadingKpis ? '...' : (kpis?.pendencias_aso ?? 0).toLocaleString('pt-BR')}
            </span>
            <span className="text-xs text-gray-500">sem data / vencidos</span>
          </div>
          <div className="mt-2.5 flex items-center justify-center">
            <span className="w-full py-1 px-2 text-[11px] font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded-md inline-flex items-center justify-center gap-1.5 group-hover:bg-amber-100 group-hover:border-amber-300 transition-colors text-center">
              <span>Exigem agendamento</span>
              <ArrowUpRight className="w-3.5 h-3.5 text-amber-700 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform flex-shrink-0" />
            </span>
          </div>
        </Link>
      </div>

      {/* Banner de Autosserviço e Atualização de Quadro (Design Claro e Limpo) */}
      <div className="bg-white rounded-xl p-4 sm:p-5 text-gray-800 shadow-xs border border-gray-200 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
              Autosserviço & Manutenção
            </span>
          </div>
          <h3 className="text-sm sm:text-base font-bold text-gray-900 flex items-center gap-2">
            Atualização de Quadro de Pessoas
          </h3>
          <p className="text-xs text-gray-500 max-w-2xl leading-relaxed mt-0.5">
            Resolva divergências de colaboradores com total autonomia. Inclusões retroativas diretas
            e registros de desligamento com justificativa arquivada para auditoria.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2.5 flex-shrink-0 w-full md:w-auto">
          <button
            type="button"
            onClick={() => setIsRetroModalOpen(true)}
            className="flex-1 md:flex-none px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg shadow-xs flex items-center justify-center gap-2 transition-colors cursor-pointer"
          >
            <UserPlus className="w-4 h-4" />
            Sentiu falta de alguém?
          </button>
          <button
            type="button"
            onClick={() => handleOpenDesligamento()}
            className="flex-1 md:flex-none px-4 py-2.5 bg-white hover:bg-rose-50 text-rose-700 hover:text-rose-800 border border-rose-200 hover:border-rose-300 text-xs font-bold rounded-lg shadow-xs flex items-center justify-center gap-2 transition-colors cursor-pointer"
          >
            <UserMinus className="w-4 h-4 text-rose-600" />
            Alguém não deveria estar aqui?
          </button>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="border-b border-gray-200 flex items-center justify-between overflow-x-auto">
        <nav className="flex space-x-6 sm:space-x-8 shrink-0" aria-label="Abas">
          <button
            onClick={() => handleTabChange('organograma')}
            className={`py-3 px-1 border-b-2 font-medium text-sm flex items-center gap-2 transition-colors ${
              activeTab === 'organograma'
                ? 'border-primary-600 text-primary-600 font-semibold'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <GitBranch className="w-4 h-4" />
            <span>Organograma</span>
          </button>

          <button
            onClick={() => handleTabChange('pessoas')}
            className={`py-3 px-1 border-b-2 font-medium text-sm flex items-center gap-2 transition-colors ${
              activeTab === 'pessoas'
                ? 'border-primary-600 text-primary-600 font-semibold'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Users className="w-4 h-4" />
            <span>Pessoas</span>
          </button>

          <button
            onClick={() => handleTabChange('movimentacoes')}
            className={`py-3 px-1 border-b-2 font-medium text-sm flex items-center gap-2 transition-colors ${
              activeTab === 'movimentacoes'
                ? 'border-primary-600 text-primary-600 font-semibold'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Clock className="w-4 h-4" />
            <span>Movimentações</span>
          </button>
        </nav>

        {activeTab === 'movimentacoes' && (
          <div className="flex items-center gap-1.5 pb-2">
            {(['hoje', 'semana', 'mes', 'ano', 'tudo'] as const).map((p) => (
              <button
                key={p}
                onClick={() => setMovPeriodo(p)}
                className={`px-2.5 py-1 text-xs rounded-md font-medium transition-colors ${
                  movPeriodo === p
                    ? 'bg-primary-600 text-white shadow-2xs'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {p === 'hoje'
                  ? 'Hoje'
                  : p === 'semana'
                  ? 'Semana'
                  : p === 'mes'
                  ? 'Mês'
                  : p === 'ano'
                  ? 'Ano'
                  : 'Todas'}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* TAB CONTENT: ORGANOGRAMA */}
      {activeTab === 'organograma' && (
        <div className="space-y-6">
          {loadingData ? (
            <div className="bg-white rounded-xl p-12 text-center border border-gray-200">
              <RefreshCw className="w-8 h-8 text-primary-500 animate-spin mx-auto mb-3" />
              <p className="text-gray-500 text-sm font-medium">Carregando árvore organizacional...</p>
            </div>
          ) : organograma?.setores?.length === 0 ? (
            <div className="bg-white rounded-xl p-12 text-center border border-gray-200">
              <Layers className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <h3 className="text-base font-semibold text-gray-900">Nenhum setor ou cargo configurado</h3>
              <p className="text-sm text-gray-500 mt-1 max-w-md mx-auto">
                Cadastre setores e colaboradores para gerar a estrutura organizacional completa da empresa.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Nível 1: Empresa Root Card */}
              <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-xs">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-center space-x-3.5">
                    <div className="w-12 h-12 rounded-xl bg-primary-50 text-primary-700 flex items-center justify-center font-bold text-lg border border-primary-200">
                      <Building2 className="w-6 h-6" />
                    </div>
                    <div>
                      <div className="flex items-center space-x-2">
                        <span className="text-xs font-semibold uppercase tracking-wider text-primary-700 bg-primary-50 border border-primary-100 px-2 py-0.5 rounded">
                          Nível 1 · Empresa
                        </span>
                        {(organograma?.cnpj || empresa?.cnpj) && (
                          <span className="text-xs text-gray-500 font-medium">
                            CNPJ {formatCNPJ(organograma?.cnpj || empresa?.cnpj)}
                          </span>
                        )}
                      </div>
                      <h2 className="text-lg font-bold text-gray-900 mt-0.5">
                        {organograma?.razao_social || empresa?.nome || 'Empresa'}
                      </h2>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 text-xs sm:text-sm text-gray-600 bg-gray-50 rounded-lg p-2.5 border border-gray-200">
                    <div>
                      <span className="text-gray-400 block text-xs">Setores</span>
                      <strong className="text-gray-900 font-semibold">
                        {organograma?.setores ? organograma.setores.length : 0}
                      </strong>
                    </div>
                    <div className="h-6 w-px bg-gray-200" />
                    <div>
                      <span className="text-gray-400 block text-xs">Colaboradores</span>
                      <strong className="text-primary-700 font-bold">
                        {(organograma?.setores || []).reduce((acc, s) => acc + s.total_colaboradores, 0).toLocaleString('pt-BR')}
                      </strong>
                    </div>
                  </div>
                </div>
              </div>

              {/* Barra de Filtro Rápido do Organograma */}
              <div className="flex flex-wrap items-center justify-between gap-3 bg-white border border-gray-200 rounded-xl px-4 py-3 shadow-2xs">
                <Toggle
                  checked={apenasComOcupantes}
                  onChange={setApenasComOcupantes}
                  label="Apenas setores com ocupantes"
                  color="primary"
                  size="sm"
                />

                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span className="font-semibold text-gray-800">
                      Exibindo {setoresFiltrados.length} de {organograma?.setores?.length || 0} setor(es)
                    </span>
                    {apenasComOcupantes && (
                      <span className="bg-primary-50 text-primary-700 border border-primary-200 px-2 py-0.5 rounded-full font-medium">
                        Ocultando setores vazios
                      </span>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() => setIsCriarSetorModalOpen(true)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-primary-600 hover:bg-primary-700 rounded-lg shadow-2xs transition-all cursor-pointer"
                    title="Cadastrar novo setor para a empresa"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Novo Setor</span>
                  </button>
                </div>
              </div>

              {/* Setores & Hierarquia */}
              <div className="space-y-4 pl-0 sm:pl-4">
                {setoresFiltrados.length === 0 ? (
                  <div className="bg-white border border-gray-200 rounded-xl p-8 text-center">
                    <Layers className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm font-medium text-gray-700">Nenhum setor encontrado com os filtros atuais</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {apenasComOcupantes
                        ? 'Todos os setores cadastrados nesta empresa estão atualmente sem ocupantes (0 colaboradores).'
                        : 'Ajuste os termos de busca ou filtros de setor.'}
                    </p>
                  </div>
                ) : (
                  setoresFiltrados.map((setor) => {
                    const isExpanded = !!expandedSetores[setor.setor_id];
                    return (
                      <div
                        key={setor.setor_id}
                        className="bg-white border border-gray-200 rounded-xl shadow-xs overflow-hidden transition-all"
                      >
                      {/* Nível 2: Setor Header */}
                      <div
                        onClick={() => toggleSetor(setor.setor_id)}
                        className="p-4 bg-gray-50/70 hover:bg-gray-100/60 cursor-pointer flex items-center justify-between border-b border-gray-200 transition-colors"
                      >
                        <div className="flex items-center space-x-3">
                          <button className="text-gray-400 hover:text-gray-600">
                            {isExpanded ? (
                              <ChevronDown className="w-5 h-5" />
                            ) : (
                              <ChevronRight className="w-5 h-5" />
                            )}
                          </button>
                          <div>
                            <div className="flex items-center space-x-2">
                              <span className="text-xs font-semibold uppercase tracking-wider text-slate-600 bg-white border border-gray-200 px-2 py-0.5 rounded">
                                Nível 2 · Setor
                              </span>
                              <span className="text-xs text-gray-500">
                                {setor.total_cargos} cargo(s)
                              </span>
                            </div>
                            <h3 className="text-base font-bold text-gray-900 mt-0.5">
                              {setor.setor_nome}
                            </h3>
                          </div>
                        </div>

                        <div className="flex items-center space-x-3">
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 border border-gray-200">
                            <Users className="w-3.5 h-3.5 mr-1" />
                            {setor.total_colaboradores} colaborador(es)
                          </span>

                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenCriarCargo(setor);
                            }}
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-primary-700 bg-white hover:bg-primary-50 border border-primary-200 hover:border-primary-300 rounded-lg shadow-2xs transition-all cursor-pointer"
                            title="Criar novo cargo dentro deste setor"
                          >
                            <Plus className="w-3.5 h-3.5" />
                            <span>+ Novo Cargo</span>
                          </button>
                        </div>
                      </div>

                      {/* Cargos do Setor (Árvore Hierárquica) */}
                      {isExpanded && (
                        <div className="p-4 bg-white space-y-3">
                          {setor.cargos_raiz.length === 0 ? (
                            <p className="text-xs text-gray-400 italic py-2">
                              Nenhum cargo associado a este setor.
                            </p>
                          ) : (
                            <div className="space-y-3">
                              {setor.cargos_raiz.map((cargoNode) => (
                                <CargoTreeItem
                                  key={cargoNode.cargo_id}
                                  node={cargoNode}
                                  setorId={setor.setor_id}
                                  setorNome={setor.setor_nome}
                                  depth={0}
                                  expandedOccupants={expandedOccupants}
                                  toggleOccupants={toggleOccupants}
                                  onOpenDetails={handleOpenCargoDetalhes}
                                  onDefinirSuperior={handleOpenDefinirSuperior}
                                  editingColaboradorId={editingColaboradorId}
                                  onToggleEditColaborador={(id) =>
                                    setEditingColaboradorId((prev) => (prev === id ? null : id))
                                  }
                                  onColaboradorSaved={handleColaboradorSaved}
                                  onDownloadAso={handleDownloadColaboradorAso}
                                  downloadingColabAsoId={downloadingColabAsoId}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                }))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* TAB CONTENT: PESSOAS (VISÃO DIRETA DE COLABORADORES) */}
      {activeTab === 'pessoas' && (
        <div className="space-y-4">
          {loadingData ? (
            <div className="bg-white rounded-xl p-12 text-center border border-gray-200">
              <RefreshCw className="w-8 h-8 text-primary-500 animate-spin mx-auto mb-3" />
              <p className="text-gray-500 text-sm font-medium">Carregando quadro de colaboradores...</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
              <div className="px-5 py-3 bg-gray-50/70 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3 text-xs text-gray-600 font-medium">
                <div className="flex flex-wrap items-center gap-3">
                  <div>
                    Mostrando <strong className="text-gray-900 font-bold">{totalPessoas === 0 ? 0 : (pessoasPage - 1) * pessoasPageSize + 1}</strong> a <strong className="text-gray-900 font-bold">{Math.min(pessoasPage * pessoasPageSize, totalPessoas)}</strong> de <strong className="text-gray-900 font-bold">{totalPessoas}</strong> colaborador(es)
                  </div>

                  {/* Segmented Control Moderno para Status */}
                  <div className="inline-flex items-center p-0.5 bg-gray-200/80 rounded-lg border border-gray-300 shadow-2xs">
                    <button
                      type="button"
                      onClick={() => {
                        setPessoasStatusFilter('all');
                        setPessoasPage(1);
                      }}
                      className={`px-3 py-1 text-xs font-semibold rounded-md transition-all cursor-pointer flex items-center gap-1.5 ${
                        pessoasStatusFilter === 'all'
                          ? 'bg-white text-gray-900 shadow-xs font-bold'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                      title="Exibir todos os colaboradores"
                    >
                      <span>Todos</span>
                      <span
                        className={`text-[10px] px-1.5 py-0.2 rounded-full font-bold ${
                          pessoasStatusFilter === 'all'
                            ? 'bg-gray-100 text-gray-800'
                            : 'bg-white/70 text-gray-500'
                        }`}
                      >
                        {statusCounts.all}
                      </span>
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setPessoasStatusFilter('ativo');
                        setPessoasPage(1);
                      }}
                      className={`px-3 py-1 text-xs font-semibold rounded-md transition-all cursor-pointer flex items-center gap-1.5 ${
                        pessoasStatusFilter === 'ativo'
                          ? 'bg-white text-emerald-800 shadow-xs font-bold'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                      title="Filtrar colaboradores com status Ativo"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                      <span>Ativos</span>
                      <span
                        className={`text-[10px] px-1.5 py-0.2 rounded-full font-bold ${
                          pessoasStatusFilter === 'ativo'
                            ? 'bg-emerald-50 text-emerald-700'
                            : 'bg-white/70 text-gray-500'
                        }`}
                      >
                        {statusCounts.ativo}
                      </span>
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setPessoasStatusFilter('candidato');
                        setPessoasPage(1);
                      }}
                      className={`px-3 py-1 text-xs font-semibold rounded-md transition-all cursor-pointer flex items-center gap-1.5 ${
                        pessoasStatusFilter === 'candidato'
                          ? 'bg-white text-amber-800 shadow-xs font-bold'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                      title="Filtrar candidatos em admissão"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                      <span>Candidatos</span>
                      <span
                        className={`text-[10px] px-1.5 py-0.2 rounded-full font-bold ${
                          pessoasStatusFilter === 'candidato'
                            ? 'bg-amber-50 text-amber-700'
                            : 'bg-white/70 text-gray-500'
                        }`}
                      >
                        {statusCounts.candidato}
                      </span>
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setPessoasStatusFilter('desligado');
                        setPessoasPage(1);
                      }}
                      className={`px-3 py-1 text-xs font-semibold rounded-md transition-all cursor-pointer flex items-center gap-1.5 ${
                        pessoasStatusFilter === 'desligado'
                          ? 'bg-white text-rose-800 shadow-xs font-bold'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                      title="Filtrar colaboradores desligados"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-rose-400"></span>
                      <span>Desligados</span>
                      <span
                        className={`text-[10px] px-1.5 py-0.2 rounded-full font-bold ${
                          pessoasStatusFilter === 'desligado'
                            ? 'bg-rose-50 text-rose-700'
                            : 'bg-white/70 text-gray-500'
                        }`}
                      >
                        {statusCounts.desligado}
                      </span>
                    </button>
                  </div>

                  {/* Campo de Busca Rápida na Aba Pessoas */}
                  <div className="relative min-w-[200px] max-w-xs">
                    <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <input
                      type="text"
                      placeholder="Buscar por nome ou CPF..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-8 pr-7 py-1 text-xs bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none placeholder-gray-400 text-gray-800"
                    />
                    {searchQuery && (
                      <button
                        type="button"
                        onClick={() => setSearchQuery('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-0.5 cursor-pointer"
                        title="Limpar busca"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {isAdmin && hasGrupoEconomicoFiliais && (
                    <button
                      type="button"
                      onClick={() => setIsConfirmTransferAllOpen(true)}
                      disabled={colaboradoresFiltrados.length === 0}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 rounded-lg shadow-2xs transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                      title="Transferir todos os colaboradores desta filial para outro CNPJ do Grupo Econômico"
                    >
                      <ArrowRightLeft className="w-3.5 h-3.5 text-indigo-600" />
                      <span>Transferir Filial Toda</span>
                    </button>
                  )}

                  {isAdmin && (
                    <button
                      type="button"
                      onClick={handleExportColaboradoresExcel}
                      disabled={colaboradoresFiltrados.length === 0}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-primary-700 bg-primary-50 hover:bg-primary-100 border border-primary-200 rounded-lg shadow-2xs transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                      title="Exportar colaboradores exibidos para planilha Excel (.xlsx)"
                    >
                      <FileDown className="w-3.5 h-3.5" />
                      <span>Exportar Excel (.xlsx)</span>
                    </button>
                  )}
                </div>
              </div>

              {colaboradoresFiltrados.length === 0 ? (
                <div className="p-12 text-center">
                  <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <h3 className="text-base font-semibold text-gray-900">Nenhum colaborador encontrado</h3>
                  <p className="text-sm text-gray-500 mt-1">Ajuste os filtros de busca por nome, CPF ou status.</p>
                  {(pessoasStatusFilter !== 'all' || searchQuery) && (
                    <button
                      type="button"
                      onClick={() => {
                        setPessoasStatusFilter('all');
                        setSearchQuery('');
                        setPessoasPage(1);
                      }}
                      className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-primary-700 bg-primary-50 hover:bg-primary-100 border border-primary-200 rounded-lg shadow-2xs transition-colors cursor-pointer"
                    >
                      Limpar Filtros
                    </button>
                  )}
                </div>
              ) : (
                <>
                  {/* Desktop Table (mantida 100% idêntica para telas md ou maiores) */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 text-xs sm:text-sm">
                  <thead className="bg-gray-50 text-gray-600 font-semibold uppercase text-xs tracking-wider">
                    <tr>
                      <th
                        scope="col"
                        onClick={() => handleSort('nome')}
                        className="px-3 py-2.5 text-left cursor-pointer hover:bg-gray-100 transition-colors select-none group"
                        title="Clique para ordenar por nome"
                      >
                        <div className="inline-flex items-center gap-1.5 font-semibold text-gray-700 group-hover:text-gray-900">
                          <span>Colaborador</span>
                          {sortField === 'nome' ? (
                            sortDirection === 'asc' ? (
                              <ArrowUp className="w-3.5 h-3.5 text-primary-600" />
                            ) : (
                              <ArrowDown className="w-3.5 h-3.5 text-primary-600" />
                            )
                          ) : (
                            <ArrowUpDown className="w-3.5 h-3.5 text-gray-300 opacity-60 group-hover:opacity-100" />
                          )}
                        </div>
                      </th>
                      <th scope="col" className="px-3 py-2.5 text-left">CPF</th>
                      <th
                        scope="col"
                        onClick={() => handleSort('setor_nome')}
                        className="px-3 py-2.5 text-left cursor-pointer hover:bg-gray-100 transition-colors select-none group"
                        title="Clique para ordenar por setor"
                      >
                        <div className="inline-flex items-center gap-1.5 font-semibold text-gray-700 group-hover:text-gray-900">
                          <span>Setor</span>
                          {sortField === 'setor_nome' ? (
                            sortDirection === 'asc' ? (
                              <ArrowUp className="w-3.5 h-3.5 text-primary-600" />
                            ) : (
                              <ArrowDown className="w-3.5 h-3.5 text-primary-600" />
                            )
                          ) : (
                            <ArrowUpDown className="w-3.5 h-3.5 text-gray-300 opacity-60 group-hover:opacity-100" />
                          )}
                        </div>
                      </th>
                      <th
                        scope="col"
                        onClick={() => handleSort('cargo_nome')}
                        className="px-3 py-2.5 text-left cursor-pointer hover:bg-gray-100 transition-colors select-none group"
                        title="Clique para ordenar por cargo"
                      >
                        <div className="inline-flex items-center gap-1.5 font-semibold text-gray-700 group-hover:text-gray-900">
                          <span>Cargo</span>
                          {sortField === 'cargo_nome' ? (
                            sortDirection === 'asc' ? (
                              <ArrowUp className="w-3.5 h-3.5 text-primary-600" />
                            ) : (
                              <ArrowDown className="w-3.5 h-3.5 text-primary-600" />
                            )
                          ) : (
                            <ArrowUpDown className="w-3.5 h-3.5 text-gray-300 opacity-60 group-hover:opacity-100" />
                          )}
                        </div>
                      </th>
                      <th
                        scope="col"
                        onClick={() => handleSort('data_admissao')}
                        className="px-3 py-2.5 text-left cursor-pointer hover:bg-gray-100 transition-colors select-none group"
                        title="Clique para ordenar por data de admissão"
                      >
                        <div className="inline-flex items-center gap-1.5 font-semibold text-gray-700 group-hover:text-gray-900">
                          <span>Data Admissão</span>
                          {sortField === 'data_admissao' ? (
                            sortDirection === 'asc' ? (
                              <ArrowUp className="w-3.5 h-3.5 text-primary-600" />
                            ) : (
                              <ArrowDown className="w-3.5 h-3.5 text-primary-600" />
                            )
                          ) : (
                            <ArrowUpDown className="w-3.5 h-3.5 text-gray-300 opacity-60 group-hover:opacity-100" />
                          )}
                        </div>
                      </th>
                      <th scope="col" className="px-3 py-2.5 text-center">
                        <div
                          className="inline-flex items-center justify-center gap-1 group relative cursor-pointer"
                          onClick={() => setShowStatusSstTooltip((prev) => !prev)}
                        >
                          <span>Status SST</span>
                          <Info className="w-3.5 h-3.5 text-gray-400 group-hover:text-primary-600 transition-colors" />

                          {/* Hint explicativo no Hover/Click apontando para BAIXO para nunca ser cortado pelo overflow-hidden */}
                          <div
                            className={`${
                              showStatusSstTooltip ? 'visible opacity-100' : 'invisible group-hover:visible opacity-0 group-hover:opacity-100'
                            } transition-all duration-200 absolute z-50 top-full mt-2.5 right-0 sm:left-1/2 sm:-translate-x-1/2 w-72 p-3.5 bg-gray-900 text-white text-xs rounded-xl shadow-2xl font-normal normal-case text-left`}
                          >
                            <div className="font-semibold text-white mb-2 flex items-center justify-between text-[11px] uppercase tracking-wider text-primary-300 border-b border-gray-700/80 pb-1">
                              <span>Vigência Periódica (NR-7)</span>
                              <span className="text-[10px] text-gray-400 font-normal">Padrão 12 meses</span>
                            </div>
                            <div className="space-y-2 text-[11px] leading-tight text-gray-200">
                              <div className="flex items-start gap-2">
                                <span className="w-2 h-2 rounded-full bg-blue-400 mt-0.5 flex-shrink-0" />
                                <span><strong>Agendado:</strong> Exame agendado ou em andamento.</span>
                              </div>
                              <div className="flex items-start gap-2">
                                <span className="w-2 h-2 rounded-full bg-emerald-400 mt-0.5 flex-shrink-0" />
                                <span><strong>Em dia:</strong> Último exame em até 300 dias. Conforme NR-7.</span>
                              </div>
                              <div className="flex items-start gap-2">
                                <span className="w-2 h-2 rounded-full bg-amber-400 mt-0.5 flex-shrink-0" />
                                <span><strong>A Vencer:</strong> Janela de 60 dias (301-365d). Recomenda-se agendar.</span>
                              </div>
                              <div className="flex items-start gap-2">
                                <span className="w-2 h-2 rounded-full bg-rose-400 mt-0.5 flex-shrink-0" />
                                <span><strong>Exame Vencido:</strong> Mais de 365 dias (fora de conformidade).</span>
                              </div>
                              <div className="flex items-start gap-2">
                                <span className="w-2 h-2 rounded-full bg-gray-400 mt-0.5 flex-shrink-0" />
                                <span><strong>Sem Data:</strong> Sem exame ocupacional registrado.</span>
                              </div>
                            </div>
                            {/* Seta do tooltip apontando para CIMA */}
                            <div className="absolute bottom-full right-4 sm:left-1/2 sm:-translate-x-1/2 border-6 border-transparent border-b-gray-900" />
                          </div>
                        </div>
                      </th>
                      <th scope="col" className="px-3 py-2.5 text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {colaboradoresPaginados.map((colab) => {
                      const isHighlighted = highlightedColaboradorId === colab.id;
                      const isBloqueadoMudanca =
                        isHighlighted ||
                        Boolean(
                          colab.bloqueio_mudanca_funcao_ate &&
                            new Date(colab.bloqueio_mudanca_funcao_ate).getTime() > Date.now(),
                        );

                      return (
                      <React.Fragment key={colab.id}>
                        <tr
                          id={`colab-row-${colab.id}`}
                          className={`transition-all duration-500 ${
                            isHighlighted
                              ? 'bg-emerald-50/50 border-l-4 border-l-emerald-500 shadow-xs'
                              : 'hover:bg-gray-50/70 border-l-4 border-l-transparent'
                          }`}
                        >
                          <td className="px-3 py-2.5 max-w-[200px]">
                            <div className="flex flex-col">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span
                                  className="font-bold text-gray-900 truncate block max-w-[180px]"
                                  title={colab.nome_social && colab.nome_social.trim() !== colab.nome.trim() ? `${colab.nome_social} (${colab.nome})` : colab.nome}
                                >
                                  {colab.nome_social && colab.nome_social.trim() !== colab.nome.trim()
                                    ? colab.nome_social
                                    : colab.nome}
                                </span>
                                {colab.pcd && (
                                  <span
                                    className="inline-flex items-center px-1.5 py-0.2 rounded text-[10px] font-bold bg-purple-50 text-purple-700 border border-purple-200"
                                    title="Pessoa com Deficiência (PCD)"
                                  >
                                    PCD
                                  </span>
                                )}
                                {colab.enviar_esocial === false && (
                                  <span
                                    className="inline-flex items-center px-1.5 py-0.2 rounded text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200"
                                    title="Envio de eventos ao eSocial desabilitado"
                                  >
                                    Sem eSocial
                                  </span>
                                )}
                              </div>
                              {isHighlighted && (
                                <div className="inline-flex items-center gap-1.5 mt-0.5 text-[11px] font-semibold text-emerald-700">
                                  <span className="relative flex h-2 w-2">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                                  </span>
                                  <span>Movimentado recentemente</span>
                                </div>
                              )}
                              {colab.mudanca_funcao_em_andamento && (
                                <div className="inline-flex items-center gap-1 mt-0.5 px-2 py-0.2 rounded-full text-[10px] font-bold bg-amber-50 text-amber-800 border border-amber-300 shadow-2xs">
                                  <span className="relative flex h-1.5 w-1.5">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
                                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-amber-500" />
                                  </span>
                                  <span>Mudança ➔ {colab.mudanca_funcao_novo_cargo || 'Novo Cargo'}</span>
                                </div>
                              )}
                              {!isHighlighted && !colab.mudanca_funcao_em_andamento && isBloqueadoMudanca && (
                                <div className="inline-flex items-center gap-1 mt-0.5 text-[10px] font-medium text-slate-500">
                                  <Clock className="w-3 h-3 text-slate-400" />
                                  <span>Alterado nas últimas 24h</span>
                                </div>
                              )}
                              {colab.matricula && (
                                <div className="text-[11px] text-gray-400 mt-0.5">Matrícula: {colab.matricula}</div>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-2.5 whitespace-nowrap font-mono text-xs text-gray-600">
                            {formatCPF(colab.cpf)}
                          </td>
                          <td
                            className="px-3 py-2.5 max-w-[130px] truncate text-gray-700 font-medium text-xs"
                            title={colab.setor_nome || '-'}
                          >
                            {colab.setor_nome || '-'}
                          </td>
                          <td
                            className="px-3 py-2.5 max-w-[140px] truncate text-xs"
                            title={colab.cargo_nome || '-'}
                          >
                            <button
                              type="button"
                              onClick={() =>
                                handleOpenCargoDetalhes(
                                  colab.cargo_id,
                                  colab.setor_id,
                                  colab.setor_nome,
                                  colab.cargo_nome,
                                )
                              }
                              className="text-left font-semibold text-primary-700 hover:text-primary-900 hover:underline cursor-pointer flex items-center gap-1 group truncate"
                              title="Clique para ver os parâmetros e ficha de SST deste cargo"
                            >
                              <span className="truncate">{colab.cargo_nome || '-'}</span>
                              <ArrowUpRight className="w-3 h-3 text-primary-400 group-hover:text-primary-600 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                            </button>
                            {colab.mudanca_funcao_em_andamento && colab.mudanca_funcao_novo_cargo && (
                              <div className="text-[10px] text-amber-700 font-semibold mt-0.5 flex items-center gap-1">
                                <ArrowRightLeft className="w-3 h-3 text-amber-600 shrink-0" />
                                <span>Futuro: {colab.mudanca_funcao_novo_cargo}</span>
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2.5 whitespace-nowrap text-xs text-gray-600">
                            {colab.data_admissao
                              ? new Date(colab.data_admissao + 'T12:00:00').toLocaleDateString('pt-BR')
                              : 'Não informada'}
                          </td>
                          <td className="px-3 py-2.5 text-center whitespace-nowrap">
                            {colab.status === 'desligado' ? (
                              <span
                                title="Colaborador desligado da empresa."
                                className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-rose-50 text-rose-700 border border-rose-200"
                              >
                                <span className="w-1.5 h-1.5 rounded-full bg-rose-500 mr-1.5" />
                                Desligado
                              </span>
                            ) : colab.status === 'candidato' ? (
                              <span
                                title="Candidato em processo de admissão."
                                className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-purple-50 text-purple-700 border border-purple-200"
                              >
                                <span className="w-1.5 h-1.5 rounded-full bg-purple-500 mr-1.5" />
                                Candidato
                              </span>
                            ) : (
                              <>
                                {colab.status_periodico === 'em_dia' && (
                                  <span
                                    title="Conforme NR-7: Último periódico realizado há menos de 300 dias."
                                    className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 cursor-help"
                                  >
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mr-1.5" />
                                    Em dia
                                  </span>
                                )}
                                {colab.status_periodico === 'a_vencer' && (
                                  <span
                                    title="Alerta preventivo: Janela de renovação de 60 dias da NR-7 (entre 301 e 365 dias)."
                                    className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-50 text-amber-700 border border-amber-200 cursor-help"
                                  >
                                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mr-1.5" />
                                    A Vencer
                                  </span>
                                )}
                                {colab.status_periodico === 'vencido' && (
                                  <span
                                    title="Não conformidade NR-7: Último exame realizado há mais de 365 dias (vencido)."
                                    className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-rose-50 text-rose-700 border border-rose-200 cursor-help"
                                  >
                                    <span className="w-1.5 h-1.5 rounded-full bg-rose-500 mr-1.5" />
                                    Exame Vencido
                                  </span>
                                )}
                                {colab.status_periodico === 'agendado' && (
                                  <span
                                    title="Ação em andamento: Colaborador com exame agendado ou atendimento em curso."
                                    className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-blue-50 text-blue-700 border border-blue-200 cursor-help"
                                  >
                                    <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mr-1.5" />
                                    Agendado
                                  </span>
                                )}
                                {(!colab.status_periodico || colab.status_periodico === 'sem_data') && (
                                  <span
                                    title="Pendente: Colaborador ativo sem data de exame ocupacional informada."
                                    className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-700 border border-gray-200 cursor-help"
                                  >
                                    Sem Data
                                  </span>
                                )}
                              </>
                            )}
                          </td>
                          <td className="px-3 py-2.5 text-right whitespace-nowrap">
                            <div className="flex items-center justify-end gap-1.5">
                              {colab.mudanca_funcao_em_andamento ? (
                                <button
                                  type="button"
                                  disabled
                                  title={`Mudança de função em andamento para "${colab.mudanca_funcao_novo_cargo || 'Novo Cargo'}". Aguardando a realização e conclusão do exame ocupacional.`}
                                  className="text-[11px] font-semibold text-amber-800 bg-amber-50 px-2 py-1 rounded-lg border border-amber-300 cursor-not-allowed inline-flex items-center gap-1 opacity-90 shadow-2xs"
                                >
                                  <Clock className="w-3 h-3 text-amber-600 animate-pulse" />
                                  <span>Aguardando</span>
                                </button>
                              ) : isBloqueadoMudanca ? (
                                <button
                                  type="button"
                                  disabled
                                  title={`Mudança de função realizada recentemente em ${
                                    colab.data_ultima_mudanca_funcao
                                      ? new Date(colab.data_ultima_mudanca_funcao).toLocaleDateString('pt-BR')
                                      : 'hoje'
                                  }. Aguarde 24 horas para uma nova alteração (liberação em ${
                                    new Date(colab.bloqueio_mudanca_funcao_ate!).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                                  }).`}
                                  className="text-[11px] font-medium text-gray-400 bg-gray-100 px-2 py-1 rounded-lg border border-gray-200 cursor-not-allowed inline-flex items-center gap-1 opacity-70"
                                >
                                  <Clock className="w-3 h-3 text-gray-400" />
                                  <span>Bloq. (24h)</span>
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => handleOpenMudancaFuncao(colab)}
                                  title="Mudar função ou setor deste colaborador com validação de riscos e exames"
                                  className="text-[11px] font-semibold text-primary-700 bg-white hover:bg-primary-50 px-2 py-1 rounded-lg border border-primary-200 hover:border-primary-300 transition-colors inline-flex items-center gap-1 cursor-pointer shadow-2xs"
                                >
                                  <ArrowRightLeft className="w-3 h-3 text-primary-600" />
                                  <span>Função</span>
                                </button>
                              )}

                              {hasGrupoEconomicoFiliais && (
                                <button
                                  type="button"
                                  onClick={() => handleOpenTransferir(colab)}
                                  title="Transferir colaborador para outro CNPJ/Filial do Grupo Econômico"
                                  className="text-[11px] font-semibold text-indigo-700 bg-white hover:bg-indigo-50 px-2 py-1 rounded-lg border border-indigo-200 hover:border-indigo-300 transition-colors inline-flex items-center gap-1 cursor-pointer shadow-2xs"
                                >
                                  <Building2 className="w-3 h-3 text-indigo-600" />
                                  <span>Transf. CNPJ</span>
                                </button>
                              )}

                              {colab.has_aso ? (
                                <button
                                  type="button"
                                  onClick={() => handleDownloadColaboradorAso(colab.id, colab.nome)}
                                  disabled={downloadingColabAsoId === colab.id}
                                  title="Baixar ASO em PDF"
                                  className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 px-2 py-1 rounded-lg border border-emerald-300 hover:border-emerald-400 transition-colors inline-flex items-center gap-1 cursor-pointer shadow-2xs disabled:opacity-50"
                                >
                                  {downloadingColabAsoId === colab.id ? (
                                    <RefreshCw className="w-3 h-3 animate-spin text-emerald-600" />
                                  ) : (
                                    <FileDown className="w-3 h-3 text-emerald-600" />
                                  )}
                                  ASO
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  disabled
                                  title="Nenhum ASO emitido para este colaborador"
                                  className="text-[11px] font-semibold text-gray-400 bg-gray-50 px-2 py-1 rounded-lg border border-gray-200 inline-flex items-center gap-1 cursor-not-allowed opacity-60"
                                >
                                  <FileDown className="w-3 h-3 text-gray-400" />
                                  ASO
                                </button>
                              )}
                              <button
                                onClick={() =>
                                  setEditingColaboradorId(
                                    editingColaboradorId === colab.id ? null : colab.id,
                                  )
                                }
                                title={editingColaboradorId === colab.id ? 'Fechar ficha do colaborador' : 'Ver ficha completa do colaborador'}
                                className={`text-[11px] font-semibold px-2 py-1 rounded-lg border transition-colors inline-flex items-center gap-1 ${
                                  editingColaboradorId === colab.id
                                    ? 'bg-primary-600 text-white border-primary-600'
                                    : 'text-primary-700 bg-primary-50 border-primary-200 hover:bg-primary-100'
                                }`}
                              >
                                <Edit3 className="w-3 h-3" />
                                {editingColaboradorId === colab.id ? 'Fechar' : 'Ficha'}
                              </button>
                            </div>
                          </td>
                        </tr>
                        {editingColaboradorId === colab.id && (
                          <tr>
                            <td colSpan={7} className="p-0 bg-gray-50/50">
                              <div className="p-4 border-t border-b border-gray-200">
                                <ColaboradorFichaAccordion
                                  colaboradorId={colab.id}
                                  cargoNome={colab.cargo_nome}
                                  setorNome={colab.setor_nome}
                                  onClose={() => setEditingColaboradorId(null)}
                                  onSaved={handleColaboradorSaved}
                                />
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards (exibido apenas em telas menores que md) */}
              <div className="block md:hidden divide-y divide-gray-100 bg-white">
                {colaboradoresPaginados.map((colab) => {
                  const isHighlighted = highlightedColaboradorId === colab.id;
                  const isBloqueadoMudanca =
                    isHighlighted ||
                    Boolean(
                      colab.bloqueio_mudanca_funcao_ate &&
                        new Date(colab.bloqueio_mudanca_funcao_ate).getTime() > Date.now(),
                    );

                  return (
                  <div
                    key={colab.id}
                    id={`colab-card-${colab.id}`}
                    className={`p-4 space-y-3 transition-all duration-500 ${
                      isHighlighted
                        ? 'bg-emerald-50/60 border-l-4 border-l-emerald-500 shadow-xs'
                        : 'hover:bg-gray-50/50 border-l-4 border-l-transparent'
                    }`}
                  >
                    {/* Topo do card: Nome + Status SST Badge */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <h4 className="font-bold text-gray-900 text-sm truncate">
                            {colab.nome_social && colab.nome_social.trim() !== colab.nome.trim()
                              ? colab.nome_social
                              : colab.nome}
                          </h4>
                          {colab.pcd && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-purple-50 text-purple-700 border border-purple-200">
                              PCD
                            </span>
                          )}
                          {colab.enviar_esocial === false && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-50 text-amber-700 border border-amber-200">
                              Sem eSocial
                            </span>
                          )}
                        </div>
                        {isHighlighted && (
                          <div className="inline-flex items-center gap-1.5 mt-1 text-[11px] font-semibold text-emerald-700">
                            <span className="relative flex h-2 w-2">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                            </span>
                            <span>Movimentado recentemente</span>
                          </div>
                        )}
                        {colab.mudanca_funcao_em_andamento && (
                          <div className="inline-flex items-center gap-1.5 mt-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-50 text-amber-800 border border-amber-300">
                            <span className="relative flex h-2 w-2">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
                              <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500" />
                            </span>
                            <span>Mudança em Andamento ➔ {colab.mudanca_funcao_novo_cargo || 'Novo Cargo'}</span>
                          </div>
                        )}
                        {!isHighlighted && !colab.mudanca_funcao_em_andamento && isBloqueadoMudanca && (
                          <div className="inline-flex items-center gap-1 mt-1 text-[11px] font-medium text-slate-500">
                            <Clock className="w-3 h-3 text-slate-400" />
                            <span>Função alterada nas últimas 24h</span>
                          </div>
                        )}
                        {colab.nome_social && colab.nome_social.trim() !== colab.nome.trim() && (
                          <p className="text-[11px] text-gray-500 mt-0.5">Nome civil: {colab.nome}</p>
                        )}
                        {colab.matricula && (
                          <p className="text-[11px] text-gray-400 mt-0.5">Matrícula: {colab.matricula}</p>
                        )}
                      </div>

                      {/* Status SST Badge */}
                      <div className="shrink-0">
                        {colab.status === 'desligado' ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-50 text-rose-700 border border-rose-200">
                            <span className="w-1.5 h-1.5 rounded-full bg-rose-500 mr-1" />
                            Desligado
                          </span>
                        ) : colab.status === 'candidato' ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-purple-50 text-purple-700 border border-purple-200">
                            <span className="w-1.5 h-1.5 rounded-full bg-purple-500 mr-1" />
                            Candidato
                          </span>
                        ) : (
                          <>
                            {colab.status_periodico === 'em_dia' && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mr-1" />
                                Em dia
                              </span>
                            )}
                            {colab.status_periodico === 'a_vencer' && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200">
                                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mr-1" />
                                A Vencer
                              </span>
                            )}
                            {colab.status_periodico === 'vencido' && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-50 text-rose-700 border border-rose-200">
                                <span className="w-1.5 h-1.5 rounded-full bg-rose-500 mr-1" />
                                Vencido
                              </span>
                            )}
                            {colab.status_periodico === 'agendado' && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-200">
                                <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mr-1" />
                                Agendado
                              </span>
                            )}
                            {(!colab.status_periodico || colab.status_periodico === 'sem_data') && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-100 text-gray-700 border border-gray-200">
                                Sem Data
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    {/* Informações de Cargo, Setor e CPF */}
                    <div className="grid grid-cols-2 gap-2 text-xs bg-gray-50/70 p-2.5 rounded-xl border border-gray-100">
                      <div>
                        <span className="text-[10px] font-medium text-gray-400 block uppercase">Cargo</span>
                        <button
                          type="button"
                          onClick={() =>
                            handleOpenCargoDetalhes(
                              colab.cargo_id,
                              colab.setor_id,
                              colab.setor_nome,
                              colab.cargo_nome,
                            )
                          }
                          className="font-semibold text-primary-700 hover:underline text-[11px] truncate block text-left"
                          title="Clique para ver SST"
                        >
                          {colab.cargo_nome || '-'}
                        </button>
                        {colab.mudanca_funcao_em_andamento && colab.mudanca_funcao_novo_cargo && (
                          <span className="text-[10px] text-amber-700 font-semibold truncate block mt-0.5">
                            ➔ {colab.mudanca_funcao_novo_cargo}
                          </span>
                        )}
                      </div>
                      <div>
                        <span className="text-[10px] font-medium text-gray-400 block uppercase">Setor</span>
                        <span className="font-semibold text-gray-800 text-[11px] truncate block" title={colab.setor_nome || '-'}>
                          {colab.setor_nome || '-'}
                        </span>
                      </div>
                      <div>
                        <span className="text-[10px] font-medium text-gray-400 block uppercase">CPF</span>
                        <span className="font-mono text-gray-700 text-[11px]">{formatCPF(colab.cpf)}</span>
                      </div>
                      <div>
                        <span className="text-[10px] font-medium text-gray-400 block uppercase">Admissão</span>
                        <span className="text-gray-700 text-[11px]">
                          {colab.data_admissao
                            ? new Date(colab.data_admissao + 'T12:00:00').toLocaleDateString('pt-BR')
                            : 'Não informada'}
                        </span>
                      </div>
                    </div>

                    {/* Botões de Ação Touch */}
                    <div className="flex items-center justify-end gap-2 pt-1 flex-wrap">
                      {colab.mudanca_funcao_em_andamento ? (
                        <button
                          type="button"
                          disabled
                          title={`Mudança de função em andamento para "${colab.mudanca_funcao_novo_cargo || 'Novo Cargo'}". Aguardando a realização do exame ocupacional.`}
                          className="text-xs font-semibold text-amber-800 bg-amber-50 px-3 py-1.5 rounded-xl border border-amber-300 cursor-not-allowed inline-flex items-center gap-1.5 opacity-90 shadow-2xs"
                        >
                          <Clock className="w-3.5 h-3.5 text-amber-600 animate-pulse" />
                          <span>Aguardando Exame</span>
                        </button>
                      ) : isBloqueadoMudanca ? (
                        <button
                          type="button"
                          disabled
                          title={`Mudança de função realizada recentemente. Bloqueado por 24 horas para conformidade regulatória.`}
                          className="text-xs font-medium text-gray-400 bg-gray-100 px-3 py-1.5 rounded-xl border border-gray-200 cursor-not-allowed inline-flex items-center gap-1.5 opacity-70"
                        >
                          <Clock className="w-3.5 h-3.5 text-gray-400" />
                          <span>Bloqueado (24h)</span>
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleOpenMudancaFuncao(colab)}
                          className="text-xs font-semibold text-primary-700 bg-white hover:bg-primary-50 px-3 py-1.5 rounded-xl border border-primary-200 transition-colors inline-flex items-center gap-1.5 active:scale-95 cursor-pointer shadow-2xs"
                        >
                          <ArrowRightLeft className="w-3.5 h-3.5 text-primary-600" />
                          <span>Mudar Função</span>
                        </button>
                      )}

                      {hasGrupoEconomicoFiliais && (
                        <button
                          type="button"
                          onClick={() => handleOpenTransferir(colab)}
                          title="Transferir colaborador para outro CNPJ/Filial do Grupo Econômico"
                          className="text-xs font-semibold text-indigo-700 bg-white hover:bg-indigo-50 px-3 py-1.5 rounded-xl border border-indigo-200 transition-colors inline-flex items-center gap-1.5 active:scale-95 cursor-pointer shadow-2xs"
                        >
                          <Building2 className="w-3.5 h-3.5 text-indigo-600" />
                          <span>Transf. CNPJ</span>
                        </button>
                      )}

                      {colab.has_aso ? (
                        <button
                          type="button"
                          onClick={() => handleDownloadColaboradorAso(colab.id, colab.nome)}
                          disabled={downloadingColabAsoId === colab.id}
                          className="text-xs font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 px-3 py-1.5 rounded-xl border border-emerald-300 transition-colors inline-flex items-center gap-1 active:scale-95 disabled:opacity-50"
                        >
                          {downloadingColabAsoId === colab.id ? (
                            <RefreshCw className="w-3.5 h-3.5 animate-spin text-emerald-600" />
                          ) : (
                            <FileDown className="w-3.5 h-3.5 text-emerald-600" />
                          )}
                          ASO
                        </button>
                      ) : null}

                      <button
                        type="button"
                        onClick={() =>
                          setEditingColaboradorId(
                            editingColaboradorId === colab.id ? null : colab.id,
                          )
                        }
                        className={`text-xs font-semibold px-3 py-1.5 rounded-xl border transition-colors inline-flex items-center gap-1.5 active:scale-95 ${
                          editingColaboradorId === colab.id
                            ? 'bg-primary-600 text-white border-primary-600'
                            : 'text-primary-700 bg-primary-50 border-primary-200 hover:bg-primary-100'
                        }`}
                      >
                        <Edit3 className="w-3.5 h-3.5" />
                        {editingColaboradorId === colab.id ? 'Fechar Ficha' : 'Ver Ficha'}
                      </button>
                    </div>

                    {/* Accordion Expandido Inline no Card Mobile */}
                    {editingColaboradorId === colab.id && (
                      <div className="pt-2 border-t border-gray-200">
                        <ColaboradorFichaAccordion
                          colaboradorId={colab.id}
                          cargoNome={colab.cargo_nome}
                          setorNome={colab.setor_nome}
                          onClose={() => setEditingColaboradorId(null)}
                          onSaved={handleColaboradorSaved}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
              </div>

              {/* Barra de Paginação */}
              {totalPessoas > 0 && (
                <div className="px-5 py-3.5 bg-gray-50/80 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-600">
                  <div className="flex items-center gap-2">
                    <span>Exibir:</span>
                    <select
                      value={pessoasPageSize}
                      onChange={(e) => {
                        setPessoasPageSize(Number(e.target.value));
                        setPessoasPage(1);
                      }}
                      className="border border-gray-300 rounded-lg px-2.5 py-1 text-xs bg-white text-gray-700 font-medium focus:ring-1 focus:ring-primary-500 focus:outline-hidden"
                    >
                      <option value={10}>10 por página</option>
                      <option value={20}>20 por página</option>
                      <option value={50}>50 por página</option>
                      <option value={100}>100 por página</option>
                    </select>
                    <span className="text-gray-400">|</span>
                    <span>
                      Página <strong>{pessoasPage}</strong> de <strong>{totalPagesPessoas}</strong>
                    </span>
                  </div>

                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      disabled={pessoasPage <= 1}
                      onClick={() => setPessoasPage((prev) => Math.max(1, prev - 1))}
                      className="inline-flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 text-gray-700 font-semibold disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer shadow-2xs"
                    >
                      <ChevronLeft className="w-3.5 h-3.5" />
                      <span>Anterior</span>
                    </button>

                    <div className="flex items-center gap-1 px-1">
                      {Array.from({ length: Math.min(5, totalPagesPessoas) }, (_, idx) => {
                        let pageNum: number;
                        if (totalPagesPessoas <= 5) {
                          pageNum = idx + 1;
                        } else if (pessoasPage <= 3) {
                          pageNum = idx + 1;
                        } else if (pessoasPage >= totalPagesPessoas - 2) {
                          pageNum = totalPagesPessoas - 4 + idx;
                        } else {
                          pageNum = pessoasPage - 2 + idx;
                        }

                        return (
                          <button
                            key={pageNum}
                            type="button"
                            onClick={() => setPessoasPage(pageNum)}
                            className={`w-7 h-7 rounded-lg text-xs font-bold transition-colors cursor-pointer ${
                              pessoasPage === pageNum
                                ? 'bg-primary-600 text-white shadow-2xs'
                                : 'bg-white hover:bg-gray-100 text-gray-700 border border-gray-200'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>

                    <button
                      type="button"
                      disabled={pessoasPage >= totalPagesPessoas}
                      onClick={() => setPessoasPage((prev) => Math.min(totalPagesPessoas, prev + 1))}
                      className="inline-flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 text-gray-700 font-semibold disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer shadow-2xs"
                    >
                      <span>Próxima</span>
                      <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )}

      {/* TAB CONTENT: MOVIMENTAÇÕES */}
      {activeTab === 'movimentacoes' && (
        <div className="space-y-4">
          {loadingData ? (
            <div className="bg-white rounded-xl p-12 text-center border border-gray-200">
              <RefreshCw className="w-8 h-8 text-primary-500 animate-spin mx-auto mb-3" />
              <p className="text-gray-500 text-sm font-medium">Carregando movimentações do período...</p>
            </div>
          ) : sortedMovimentacoes.length === 0 ? (
            <div className="bg-white rounded-xl p-12 text-center border border-gray-200">
              <Clock className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <h3 className="text-base font-semibold text-gray-900">Nenhuma movimentação no período</h3>
              <p className="text-sm text-gray-500 mt-1">
                Não foram registradas admissões ou desligamentos no intervalo selecionado.
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
              <div className="divide-y divide-gray-100">
                {sortedMovimentacoes.map((mov) => {
                  const isSemDemissional =
                    mov.tipo === 'desligamento_sem_demissional' ||
                    (mov.tipo === 'desligamento' && mov.is_ajuste_quadro);
                  const isRetroativa =
                    mov.tipo === 'admissao_retroativa' ||
                    (mov.tipo === 'admissao' && mov.is_ajuste_quadro);

                  return (
                    <div key={mov.id} className="p-4 flex flex-col sm:flex-row sm:items-center justify-between hover:bg-gray-50/60 transition-colors gap-3">
                      <div className="flex items-start space-x-3.5">
                        <div
                          className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm shrink-0 ${
                            isRetroativa
                              ? 'bg-purple-50 text-purple-700 border border-purple-200'
                              : isSemDemissional
                              ? 'bg-amber-50 text-amber-700 border border-amber-300'
                              : mov.tipo === 'mudanca_cargo'
                              ? mov.status_mudanca === 'aguardando_exame'
                                ? 'bg-amber-50 text-amber-700 border border-amber-300'
                                : mov.status_mudanca === 'reprovado'
                                ? 'bg-rose-50 text-rose-700 border border-rose-300'
                                : 'bg-cyan-50 text-cyan-700 border border-cyan-300'
                              : mov.tipo === 'admissao'
                              ? 'bg-emerald-50 text-emerald-600 border border-emerald-200'
                              : mov.tipo === 'em_contratacao'
                              ? 'bg-amber-50 text-amber-600 border border-amber-200'
                              : mov.tipo === 'desistencia'
                              ? 'bg-zinc-100 text-zinc-600 border border-zinc-200'
                              : mov.tipo === 'desligamento'
                              ? 'bg-rose-50 text-rose-600 border border-rose-200'
                              : mov.tipo === 'edicao'
                              ? 'bg-indigo-50 text-indigo-600 border border-indigo-200'
                              : 'bg-blue-50 text-blue-600 border border-blue-200'
                          }`}
                        >
                          {isRetroativa
                            ? '⚡'
                            : isSemDemissional
                            ? '⚡'
                            : mov.tipo === 'mudanca_cargo'
                            ? mov.status_mudanca === 'aguardando_exame'
                              ? '⏳'
                              : mov.status_mudanca === 'reprovado'
                              ? '✕'
                              : '⇄'
                            : mov.tipo === 'admissao'
                            ? '+'
                            : mov.tipo === 'em_contratacao'
                            ? '⏳'
                            : mov.tipo === 'desistencia'
                            ? '✕'
                            : mov.tipo === 'desligamento'
                            ? '−'
                            : mov.tipo === 'edicao'
                            ? '✎'
                            : '↻'}
                        </div>
                        <div>
                          <div className="flex items-center space-x-2 flex-wrap gap-y-1">
                            <h4 className="text-sm font-bold text-gray-900">{mov.colaborador_nome}</h4>
                            <span
                              className={`text-[11px] font-bold uppercase px-2.5 py-0.5 rounded-full ${
                                isRetroativa
                                  ? 'bg-indigo-100 text-indigo-900 border border-indigo-200'
                                  : isSemDemissional
                                  ? 'bg-amber-100 text-amber-900 border border-amber-300'
                                  : mov.tipo === 'mudanca_cargo'
                                  ? mov.status_mudanca === 'aguardando_exame'
                                    ? 'bg-amber-100 text-amber-900 border border-amber-300'
                                    : mov.status_mudanca === 'reprovado'
                                    ? 'bg-rose-100 text-rose-900 border border-rose-300'
                                    : 'bg-cyan-100 text-cyan-800 border border-cyan-200'
                                  : mov.tipo === 'admissao'
                                  ? 'bg-emerald-100 text-emerald-800'
                                  : mov.tipo === 'em_contratacao'
                                  ? 'bg-amber-100 text-amber-800'
                                  : mov.tipo === 'desistencia'
                                  ? 'bg-zinc-100 text-zinc-700'
                                  : mov.tipo === 'desligamento'
                                  ? 'bg-rose-100 text-rose-800'
                                  : mov.tipo === 'edicao'
                                  ? 'bg-indigo-100 text-indigo-800'
                                  : 'bg-blue-100 text-blue-800'
                              }`}
                            >
                              {isRetroativa
                                ? '⚡ Inclusão (Autosserviço)'
                                : isSemDemissional
                                ? '⚡ Sem Demissional'
                                : mov.tipo === 'mudanca_cargo'
                                ? mov.status_mudanca === 'aguardando_exame'
                                  ? '⏳ Mudança em Andamento'
                                  : mov.status_mudanca === 'reprovado'
                                  ? '✕ Mudança Reprovada'
                                  : 'Mudança de Função'
                                : mov.tipo === 'edicao'
                                ? 'Edição Cadastral'
                                : mov.tipo === 'em_contratacao'
                                ? 'Em Contratação'
                                : mov.tipo === 'desistencia'
                                ? 'Não Efetivado'
                                : mov.tipo === 'admissao'
                                ? 'Admissão Regular'
                                : mov.tipo === 'desligamento'
                                ? 'Desligamento com ASO'
                                : mov.tipo}
                            </span>

                            {/* Tag distintiva de Ajuste de Quadro vs Fluxo Clínico Regular */}
                            {isRetroativa || isSemDemissional ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-purple-100 text-purple-800 border border-purple-200">
                                ⚡ Ajuste de Quadro
                              </span>
                            ) : mov.tipo === 'admissao' || mov.tipo === 'desligamento' ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold text-gray-600 bg-gray-100 border border-gray-200">
                                Fluxo Clínico Regular
                              </span>
                            ) : null}
                          </div>
                          {mov.tipo === 'mudanca_cargo' ? (
                            <p
                              className={`text-xs font-medium mt-0.5 ${
                                mov.status_mudanca === 'aguardando_exame'
                                  ? 'text-amber-800'
                                  : mov.status_mudanca === 'reprovado'
                                  ? 'text-rose-800'
                                  : 'text-cyan-800'
                              }`}
                            >
                              {mov.detalhe ||
                                (mov.status_mudanca === 'aguardando_exame'
                                  ? 'Mudança de Função em andamento • Aguardando realização de Exame Ocupacional'
                                  : mov.status_mudanca === 'reprovado'
                                  ? 'Mudança de Função reprovada (Inapto no exame ocupacional)'
                                  : 'Mudança de Função efetivada no autosserviço')}
                              {mov.matricula && ` · Matrícula ${mov.matricula}`}
                              {mov.usuario_nome && (
                                <span className="text-gray-400"> · Por: <strong className="text-gray-600">{mov.usuario_nome}</strong></span>
                              )}
                            </p>
                          ) : (
                            <p className="text-xs text-gray-500 mt-0.5">
                              {isSemDemissional ? (
                                <span className="font-semibold text-amber-800">Desligamento sem Demissional (Ajuste de Quadro) · </span>
                              ) : isRetroativa ? (
                                <span className="font-semibold text-indigo-800">Inclusão de Quadro (Autosserviço Retroativo) · </span>
                              ) : mov.detalhe ? (
                                <span className="font-medium text-gray-700">{mov.detalhe} · </span>
                              ) : null}
                              {mov.cargo_nome} · {mov.setor_nome}
                              {mov.matricula && ` · Matrícula ${mov.matricula}`}
                              {mov.usuario_nome && (
                                <span className="text-gray-400"> · Alterado por: <strong className="text-gray-600">{mov.usuario_nome}</strong></span>
                              )}
                            </p>
                          )}

                          {/* Chips com antes e depois das alterações */}
                          {mov.alteracoes && mov.alteracoes.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1.5">
                              {mov.alteracoes.map((alt, idx) => (
                                <span
                                  key={idx}
                                  className={`inline-flex items-center text-[10px] px-2 py-0.5 rounded-md border ${
                                    mov.tipo === 'mudanca_cargo'
                                      ? mov.status_mudanca === 'aguardando_exame'
                                        ? 'bg-amber-50/80 border-amber-200 text-amber-900'
                                        : mov.status_mudanca === 'reprovado'
                                        ? 'bg-rose-50/80 border-rose-200 text-rose-900'
                                        : 'bg-cyan-50/80 border-cyan-200 text-cyan-900'
                                      : 'bg-gray-50 border-gray-200 text-gray-700'
                                  }`}
                                >
                                  <strong className="font-semibold mr-1">{alt.rotulo || alt.campo}:</strong>
                                  <span className="text-gray-400 line-through mr-1">{alt.anterior || '(vazio)'}</span>
                                  <span
                                    className={
                                      mov.tipo === 'mudanca_cargo'
                                        ? mov.status_mudanca === 'aguardando_exame'
                                          ? 'text-amber-800 font-bold'
                                          : mov.status_mudanca === 'reprovado'
                                          ? 'text-rose-700 font-bold'
                                          : 'text-cyan-800 font-bold'
                                        : 'text-emerald-700 font-semibold'
                                    }
                                  >
                                    ➔ {alt.novo || '(vazio)'}
                                  </span>
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="text-right sm:self-center shrink-0">
                        <span className="text-xs font-semibold text-gray-700 block">
                          {mov.data_evento ? (
                            /^\d{4}-\d{2}-\d{2}/.test(mov.data_evento)
                              ? `${mov.data_evento.split('T')[0].split('-')[2]}/${mov.data_evento.split('T')[0].split('-')[1]}/${mov.data_evento.split('T')[0].split('-')[0]}`
                              : new Date(mov.data_evento).toLocaleDateString('pt-BR')
                          ) : '-'}
                        </span>
                        <span className="text-[11px] text-gray-400 block">
                          {isSemDemissional
                            ? 'Ajuste de Quadro'
                            : isRetroativa
                            ? 'Ajuste de Quadro'
                            : mov.detalhe || 'Movimentação'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* RIGHT SIDE DRAWER: CARGO DETALHES (SST + HIERARQUIA) */}
      {isDrawerOpen && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          {/* Backdrop */}
          <div
            onClick={() => setIsDrawerOpen(false)}
            className="absolute inset-0 bg-slate-900/40 backdrop-blur-xs transition-opacity"
          />

          <div className="fixed inset-y-0 right-0 max-w-full flex pl-10">
            <div className="w-screen max-w-md bg-white shadow-2xl flex flex-col">
              {/* Drawer Header */}
              <div className="p-5 border-b border-gray-200 bg-gray-50/80 flex items-start justify-between">
                <div>
                  <span className="text-[11px] font-bold uppercase tracking-wider text-primary-700 bg-primary-50 px-2 py-0.5 rounded border border-primary-100">
                    Ficha Técnica do Cargo
                  </span>
                  <h3 className="text-lg font-bold text-gray-900 mt-1">
                    {cargoDetalhes?.cargo_nome || 'Detalhes do Cargo'}
                  </h3>
                  {cargoDetalhes?.cbo && (
                    <p className="text-xs text-gray-500 mt-0.5">CBO: {cargoDetalhes.cbo}</p>
                  )}
                </div>
                <button
                  onClick={() => setIsDrawerOpen(false)}
                  className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Drawer Content */}
              <div className="flex-1 overflow-y-auto p-5 space-y-6">
                {loadingDrawer ? (
                  <div className="py-12 text-center">
                    <RefreshCw className="w-7 h-7 text-primary-500 animate-spin mx-auto mb-2" />
                    <p className="text-xs text-gray-500 font-medium">Carregando dados ocupacionais...</p>
                  </div>
                ) : (
                  <>
                    {/* Headcount e Setor com Indicador de Prestador de Serviço */}
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-3.5 space-y-2">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-xs text-slate-500 block">Setor Principal</span>
                          <strong className="text-sm text-slate-900 font-semibold">
                            {cargoDetalhes?.setor_nome || 'Setor não informado'}
                          </strong>
                        </div>
                        <div className="text-right">
                          <span className="text-xs text-slate-500 block">Ocupantes Ativos</span>
                          <strong className="text-sm text-primary-700 font-bold">
                            {cargoDetalhes?.total_colaboradores ?? 0} colaboradores
                          </strong>
                        </div>
                      </div>
                      <div className="pt-1.5 border-t border-slate-200/70 flex items-center justify-between text-[11px]">
                        <span className="text-slate-500">Natureza do Setor:</span>
                        {cargoDetalhes?.is_prestacao_servico ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full font-bold bg-amber-100 text-amber-800 border border-amber-300">
                            Posto de Serviço (Prestador)
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full font-semibold bg-slate-200/80 text-slate-700 border border-slate-300">
                            Setor Próprio (Interno)
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Atividades Gerais / Descrição Ocupacional */}
                    {(cargoDetalhes?.atividades_gerais || cargoDetalhes?.descricao) && (
                      <div className="space-y-1.5">
                        <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500 flex items-center gap-1.5">
                          <Info className="w-3.5 h-3.5 text-primary-600" />
                          Atividades Ocupacionais
                        </h4>
                        <div className="p-3 bg-white border border-gray-200 rounded-xl text-xs text-gray-700 leading-relaxed whitespace-pre-line">
                          {cargoDetalhes.atividades_gerais || cargoDetalhes.descricao}
                        </div>
                      </div>
                    )}

                    {/* Hierarquia Operacional */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500 flex items-center gap-1.5">
                          <GitBranch className="w-3.5 h-3.5" />
                          Hierarquia Funcional
                        </h4>
                        <button
                          onClick={() => {
                            if (cargoDetalhes) {
                              handleOpenDefinirSuperior(
                                cargoDetalhes.cargo_id,
                                cargoDetalhes.cargo_nome,
                                cargoDetalhes.setor_id,
                                cargoDetalhes.superior_cargo_id,
                                cargoDetalhes.nivel,
                              );
                            }
                          }}
                          className="text-xs font-semibold text-primary-600 hover:text-primary-700 flex items-center gap-1 cursor-pointer"
                        >
                          <Edit3 className="w-3 h-3" />
                          Editar superior
                        </button>
                      </div>

                      <div className="bg-white border border-gray-200 rounded-lg p-3 space-y-2 text-xs">
                        <div>
                          <span className="text-gray-400 block text-[11px]">Superior Imediato:</span>
                          <strong className="text-gray-800 font-semibold text-sm">
                            {cargoDetalhes?.superior_cargo_nome || 'Não definido (Cargo Raiz)'}
                          </strong>
                          {cargoDetalhes?.nivel && (
                            <span className="ml-2 text-[10px] uppercase font-semibold px-2 py-0.5 bg-gray-100 text-gray-700 rounded">
                              {cargoDetalhes.nivel}
                            </span>
                          )}
                        </div>

                        {cargoDetalhes?.subordinados && cargoDetalhes.subordinados.length > 0 && (
                          <div className="pt-2 border-t border-gray-100">
                            <span className="text-gray-400 block text-[11px] mb-1">Subordinados diretos:</span>
                            <div className="space-y-1">
                              {cargoDetalhes.subordinados.map((sub) => (
                                <div
                                  key={sub.cargo_id}
                                  className="flex items-center justify-between bg-gray-50 px-2 py-1 rounded"
                                >
                                  <span className="font-medium text-gray-800">{sub.cargo_nome}</span>
                                  <span className="text-gray-500">{sub.total_ocupantes} pessoa(s)</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Alerta de Homologação Pendente */}
                    {(!cargoDetalhes?.enabled || cargoDetalhes?.status_homologacao === 'pendente_homologacao') && (
                      <div className="p-3.5 bg-amber-50 border border-amber-200 rounded-xl space-y-1 text-xs text-amber-950 shadow-2xs">
                        <div className="flex items-center gap-1.5 font-bold text-amber-900">
                          <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse shrink-0" />
                          <span>Função em Análise Técnica (Homologação SST)</span>
                        </div>
                        <p className="text-[11px] text-amber-800 leading-relaxed">
                          Esta função foi cadastrada como inédita no setor e está sob avaliação da equipe de Engenharia e Medicina de SST para classificação dos riscos ocupacionais (PGR) e exames obrigatórios da NR-07 (PCMSO). O agendamento de exames será desbloqueado assim que concluída a homologação.
                        </p>
                      </div>
                    )}

                    {/* SST: Aptidões Especiais (NRs) */}
                    <div className="space-y-2">
                      <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500 flex items-center gap-1.5">
                        <Award className="w-3.5 h-3.5 text-purple-600" />
                        Aptidões Especiais (NRs Regulamentadoras)
                      </h4>
                      {cargoDetalhes?.aptidoes && cargoDetalhes.aptidoes.length > 0 ? (
                        <div className="flex flex-wrap gap-1.5">
                          {cargoDetalhes.aptidoes.map((apt, idx) => (
                            <span
                              key={idx}
                              className="text-xs px-2.5 py-1 rounded-md font-semibold bg-purple-50 text-purple-900 border border-purple-200 flex items-center gap-1 shadow-2xs"
                            >
                              <Award className="w-3 h-3 text-purple-600" />
                              {apt}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-gray-400 italic bg-gray-50 p-2.5 rounded-lg border border-gray-200">
                          Nenhuma aptidão especial requerida (sem exigência de NR-35, NR-33 ou similares).
                        </p>
                      )}
                    </div>

                    {/* SST: Riscos Ocupacionais */}
                    <div className="space-y-2">
                      <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500 flex items-center gap-1.5">
                        <ShieldCheck className="w-3.5 h-3.5 text-amber-600" />
                        Riscos Ocupacionais (PGR)
                      </h4>
                      {cargoDetalhes?.riscos?.length === 0 ? (
                        <p className="text-xs text-gray-400 italic bg-gray-50 p-2.5 rounded-lg border border-gray-200">
                          Nenhum risco específico mapeado para este cargo.
                        </p>
                      ) : (
                        <div className="flex flex-wrap gap-1.5">
                          {cargoDetalhes?.riscos.map((r) => (
                            <span
                              key={r.id}
                              className="text-xs px-2.5 py-1 rounded-md font-medium bg-amber-50 text-amber-900 border border-amber-200 flex items-center gap-1"
                            >
                              <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                              {r.nome} {r.tipo ? `(${r.tipo})` : ''}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* SST: Exames Relacionados */}
                    <div className="space-y-2">
                      <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500 flex items-center gap-1.5">
                        <Stethoscope className="w-3.5 h-3.5 text-blue-600" />
                        Exames Relacionados (PCMSO)
                      </h4>
                      {cargoDetalhes?.exames?.length === 0 ? (
                        <p className="text-xs text-gray-400 italic bg-gray-50 p-2.5 rounded-lg border border-gray-200">
                          Nenhum exame clínico complementar obrigatório além da avaliação clínica.
                        </p>
                      ) : (
                        <div className="space-y-2">
                          {cargoDetalhes?.exames.map((ex) => (
                            <div
                              key={ex.id}
                              className="text-xs p-2.5 bg-blue-50/60 border border-blue-100 rounded-lg text-blue-950 space-y-1.5"
                            >
                              <div className="flex items-center justify-between">
                                <span className="font-semibold text-blue-900">{ex.nome}</span>
                                {ex.periodicidade_meses && (
                                  <span className="text-[11px] font-medium text-blue-700 bg-blue-100/70 px-2 py-0.5 rounded">
                                    A cada {ex.periodicidade_meses} meses
                                  </span>
                                )}
                              </div>
                              <div className="flex flex-wrap gap-1 pt-1 text-[10px]">
                                {ex.admissional && (
                                  <span className="px-1.5 py-0.2 rounded bg-emerald-100 text-emerald-800 font-semibold border border-emerald-200">
                                    Admissional
                                  </span>
                                )}
                                {Boolean(ex.periodico) && (
                                  <span className="px-1.5 py-0.2 rounded bg-blue-100 text-blue-800 font-semibold border border-blue-200">
                                    Periódico
                                  </span>
                                )}
                                {ex.demissional && (
                                  <span className="px-1.5 py-0.2 rounded bg-rose-100 text-rose-800 font-semibold border border-rose-200">
                                    Demissional
                                  </span>
                                )}
                                {ex.retorno_trabalho && (
                                  <span className="px-1.5 py-0.2 rounded bg-purple-100 text-purple-800 font-semibold border border-purple-200">
                                    Retorno
                                  </span>
                                )}
                                {ex.mudanca_riscos && (
                                  <span className="px-1.5 py-0.2 rounded bg-amber-100 text-amber-800 font-semibold border border-amber-200">
                                    Mudança Risco
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Colaboradores Ocupantes */}
                    <div className="space-y-2">
                      <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500 flex items-center gap-1.5">
                        <Users className="w-3.5 h-3.5" />
                        Colaboradores Alocados ({cargoDetalhes?.colaboradores?.length || 0})
                      </h4>
                      <div className="divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden max-h-60 overflow-y-auto">
                        {cargoDetalhes?.colaboradores?.map((c) => (
                          <div key={c.id} className="p-2.5 bg-white text-xs flex items-center justify-between">
                            <div>
                              <div className="font-semibold text-gray-900">{c.nome}</div>
                              <div className="text-[11px] text-gray-400">
                                {c.cpf ? `CPF: ${c.cpf}` : 'Sem CPF'} · Admissão:{' '}
                                {c.data_admissao ? new Date(c.data_admissao).toLocaleDateString('pt-BR') : '-'}
                              </div>
                            </div>
                            <div>
                              {c.has_aso && (
                                <button
                                  type="button"
                                  onClick={() => handleDownloadColaboradorAso(c.id, c.nome)}
                                  disabled={downloadingColabAsoId === c.id}
                                  className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 px-2 py-0.5 rounded border border-emerald-200 transition-colors cursor-pointer shadow-2xs disabled:opacity-50"
                                  title="Clique para baixar o ASO"
                                >
                                  {downloadingColabAsoId === c.id ? (
                                    <>
                                      <RefreshCw className="w-3 h-3 animate-spin" />
                                      <span>Baixando...</span>
                                    </>
                                  ) : (
                                    <>
                                      <FileDown className="w-3 h-3 text-emerald-600" />
                                      <span>Baixar ASO</span>
                                    </>
                                  )}
                                </button>
                              )}
                              {c.status_periodico === 'a_vencer' && (
                                <span className="text-[10px] font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded border border-amber-200">
                                  A vencer
                                </span>
                              )}
                              {c.status_periodico === 'vencido' && (
                                <span className="text-[10px] font-semibold text-rose-700 bg-rose-50 px-2 py-0.5 rounded border border-rose-200">
                                  Vencido
                                </span>
                              )}
                              {c.status_periodico === 'agendado' && (
                                <span className="text-[10px] font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 rounded border border-blue-200">
                                  Agendado
                                </span>
                              )}
                              {c.status_periodico === 'sem_data' && (
                                <span className="text-[10px] font-semibold text-gray-500 bg-gray-50 px-2 py-0.5 rounded border border-gray-200">
                                  Sem periódico
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Drawer Footer */}
              <div className="p-4 border-t border-gray-200 bg-gray-50 flex items-center justify-end">
                <button
                  onClick={() => setIsDrawerOpen(false)}
                  className="px-4 py-2 text-xs font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-xs"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: DEFINIR CARGO SUPERIOR (V1 SIMPLES & RESILIENTE) */}
      {isModalOpen && modalTargetCargo && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div
              onClick={() => setIsModalOpen(false)}
              className="fixed inset-0 bg-slate-900/50 backdrop-blur-xs transition-opacity"
            />

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
              &#8203;
            </span>

            <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full border border-gray-200">
              <div className="p-6">
                <div className="flex items-center justify-between pb-4 border-b border-gray-200">
                  <div className="flex items-center space-x-2 text-primary-700 font-semibold text-sm">
                    <GitBranch className="w-4 h-4" />
                    <span>Definir Superior Imediato</span>
                  </div>
                  <button
                    onClick={() => setIsModalOpen(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="mt-4 space-y-4 text-sm">
                  {/* Cargo selecionado */}
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">
                      Cargo Subordinado
                    </label>
                    <div className="p-2.5 bg-gray-50 rounded-lg border border-gray-200 font-semibold text-gray-900">
                      {modalTargetCargo.nome}
                    </div>
                  </div>

                  {/* Seleção do Cargo Superior */}
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">
                      Cargo Superior Imediato
                    </label>
                    <select
                      value={modalSuperiorId}
                      onChange={(e) => setModalSuperiorId(e.target.value ? Number(e.target.value) : '')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="">Nenhum (Cargo Raiz / Reporta à Diretoria Geral)</option>
                      {availableCargos
                        .filter((c) => c.id !== modalTargetCargo.id)
                        .map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.nome} ({c.setor_nome})
                          </option>
                        ))}
                    </select>
                    <p className="text-[11px] text-gray-500 mt-1">
                      O superior imediato será aplicado no contexto desta empresa.
                    </p>
                  </div>

                  {/* Nível Hierárquico */}
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">
                      Nível Hierárquico
                    </label>
                    <select
                      value={modalNivel}
                      onChange={(e) => setModalNivel(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="Diretoria">Diretoria</option>
                      <option value="Gerência">Gerência</option>
                      <option value="Coordenação">Coordenação</option>
                      <option value="Supervisão">Supervisão</option>
                      <option value="Especialista">Especialista / Técnico</option>
                      <option value="Operacional">Operacional</option>
                      <option value="Estágio / Jovem Aprendiz">Estágio / Jovem Aprendiz</option>
                    </select>
                  </div>
                </div>

                {/* Actions */}
                <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="px-4 py-2 text-xs font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={handleSalvarSuperior}
                    disabled={savingSuperior}
                    className="px-4 py-2 text-xs font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors shadow-sm disabled:opacity-50 flex items-center gap-1.5"
                  >
                    {savingSuperior && <RefreshCw className="w-3.5 h-3.5 animate-spin" />}
                    <span>Salvar Hierarquia</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modais de Autosserviço: Admissão Retroativa e Desligamento sem Demissional */}
      {empresa?.id && (
        <>
          <ModalAdmissaoRetroativa
            isOpen={isRetroModalOpen}
            onClose={() => setIsRetroModalOpen(false)}
            empresaId={empresa.id}
            cargos={cargosList}
            setores={setoresList}
            userName={user?.name}
            onSuccess={() => {
              handleTabChange('movimentacoes');
              loadTabData();
              loadKpis();
            }}
          />

          <ModalDesligamentoSemDemissional
            isOpen={isDesligamentoModalOpen}
            onClose={() => {
              setIsDesligamentoModalOpen(false);
              setDesligamentoTargetColaborador(null);
            }}
            empresaId={empresa.id}
            colaborador={desligamentoTargetColaborador}
            colaboradoresAtivos={colaboradoresFiltrados as unknown as Colaborador[]}
            onSuccess={() => {
              handleTabChange('movimentacoes');
              loadTabData();
              loadKpis();
            }}
          />

          <ModalCriarCargoSetor
            isOpen={isCriarCargoModalOpen}
            onClose={() => {
              setIsCriarCargoModalOpen(false);
              setCriarCargoTargetSetor(null);
            }}
            setor={criarCargoTargetSetor}
            cargosList={cargosList}
            setoresList={setoresList}
            empresaNome={empresa?.nome}
            empresaId={empresa?.id}
            onCreated={handleCargoCreated}
          />

          <ModalCriarSetor
            isOpen={isCriarSetorModalOpen}
            onClose={() => setIsCriarSetorModalOpen(false)}
            empresaId={empresa?.id}
            onCreated={handleSetorCreated}
          />

          <ModalMudancaFuncao
            isOpen={isMudancaFuncaoModalOpen}
            onClose={() => {
              setIsMudancaFuncaoModalOpen(false);
              setMudancaFuncaoTargetColaborador(null);
            }}
            colaborador={mudancaFuncaoTargetColaborador}
            setoresList={setoresList}
            empresaId={empresa?.id}
            onSuccess={handleMudancaFuncaoSuccess}
          />

          <TransferirColaboradoresModal
            isOpen={isTransferModalOpen}
            onClose={() => {
              setIsTransferModalOpen(false);
              setTransferTargetColaboradores([]);
            }}
            empresaOrigemId={empresa.id}
            empresaOrigemNome={empresa.nome}
            empresaOrigemCnpj={empresa.cnpj}
            colaboradores={transferTargetColaboradores}
            filiaisDisponiveis={filiaisDisponiveis}
            onSuccess={() => {
              loadTabData();
              loadKpis();
            }}
          />

          <ConfirmDialog
            isOpen={isConfirmTransferAllOpen}
            onClose={() => setIsConfirmTransferAllOpen(false)}
            onConfirm={() => {
              setIsConfirmTransferAllOpen(false);
              handleOpenTransferir();
            }}
            title="Confirmar Transferência de Filial Toda"
            message={`Tem certeza que deseja transferir todos os ${colaboradoresFiltrados.length} colaboradores desta filial (${empresa.nome}) para outra empresa/filial do Grupo Econômico? Esta ação selecionará todo o quadro ativo para o remanejamento em massa.`}
            confirmText="Sim, prosseguir para seleção"
            cancelText="Cancelar"
            variant="warning"
          />
        </>
      )}

      {/* Toast de Notificações */}
      <ToastContainer toasts={toast.toasts} onRemove={toast.removeToast} />
    </div>
  );
}

/**
 * Componente Recursivo de Nó de Cargo no Organograma
 */
function CargoTreeItem({
  node,
  setorId,
  setorNome,
  depth,
  expandedOccupants,
  toggleOccupants,
  onOpenDetails,
  onDefinirSuperior,
  editingColaboradorId,
  onToggleEditColaborador,
  onColaboradorSaved,
  onDownloadAso,
  downloadingColabAsoId,
}: {
  node: OrganizacaoCargoHierarquiaContract;
  setorId: number;
  setorNome?: string;
  depth: number;
  expandedOccupants: Record<number, boolean>;
  toggleOccupants: (cargoId: number) => void;
  onOpenDetails: (
    cargoId: number,
    setorId?: number,
    setorNome?: string,
    cargoNome?: string,
    cbo?: string | null,
  ) => void;
  onDefinirSuperior: (cargoId: number, cargoNome: string, setorId?: number, superiorId?: number | null, nivel?: string | null) => void;
  editingColaboradorId?: number | null;
  onToggleEditColaborador?: (colabId: number) => void;
  onColaboradorSaved?: () => void;
  onDownloadAso?: (colabId: number, colabNome: string) => void;
  downloadingColabAsoId?: number | null;
}) {
  const isOccupantsOpen = !!expandedOccupants[node.cargo_id];

  return (
    <div className={`relative ${depth > 0 ? 'ml-6 pl-4 border-l-2 border-primary-200 mt-2' : ''}`}>
      {/* Node Card */}
      <div className="bg-white border border-gray-200 hover:border-primary-300 rounded-xl p-3.5 shadow-2xs hover:shadow-xs transition-all flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5">
        <div className="flex items-center space-x-3">
          <div className="w-9 h-9 rounded-lg bg-gray-100 text-gray-700 flex items-center justify-center font-bold text-xs border border-gray-200">
            <Briefcase className="w-4 h-4" />
          </div>

          <div>
            <div className="flex items-center space-x-2">
              <span className="text-sm font-bold text-gray-900">{node.cargo_nome}</span>
              {(!node.enabled || node.status_homologacao === 'pendente_homologacao') && (
                <span className="inline-flex items-center gap-1.5 text-[10px] font-bold px-2 py-0.5 bg-amber-100 text-amber-900 border border-amber-300 rounded-full shadow-2xs">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                  <span>Aguardando Liberação SST</span>
                </span>
              )}
              {node.nivel && (
                <span className="text-[10px] uppercase font-semibold px-2 py-0.5 bg-primary-50 text-primary-700 border border-primary-100 rounded">
                  {node.nivel}
                </span>
              )}
            </div>
            <div className="flex items-center space-x-2 text-xs text-gray-500 mt-0.5">
              {node.cbo && <span>CBO {node.cbo}</span>}
              {node.superior_cargo_nome && (
                <>
                  <span>·</span>
                  <span>Reporta a: <strong className="text-gray-700">{node.superior_cargo_nome}</strong></span>
                </>
              )}
              {(!node.enabled || node.status_homologacao === 'pendente_homologacao') && (
                <>
                  <span>·</span>
                  <span className="text-amber-700 font-semibold">Agendamento bloqueado até homologação</span>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 self-end sm:self-auto">
          {/* Botão de abrir ocupantes */}
          <button
            onClick={() => toggleOccupants(node.cargo_id)}
            className="text-xs px-2.5 py-1 rounded-md bg-gray-50 hover:bg-gray-100 text-gray-700 border border-gray-200 flex items-center gap-1 transition-colors"
          >
            <Users className="w-3 h-3 text-gray-400" />
            <span>{node.total_ocupantes} ocupante(s)</span>
            <ChevronDown className={`w-3 h-3 transition-transform ${isOccupantsOpen ? 'rotate-180' : ''}`} />
          </button>

          {/* Botão de ver detalhes SST */}
          <button
            onClick={() => onOpenDetails(node.cargo_id, setorId, setorNome, node.cargo_nome, node.cbo)}
            title="Ver riscos e exames de SST"
            className="text-xs px-2.5 py-1 rounded-md bg-primary-50 hover:bg-primary-100 text-primary-700 border border-primary-100 font-semibold transition-colors cursor-pointer"
          >
            SST
          </button>

          {/* Botão de definir superior */}
          <button
            onClick={() => onDefinirSuperior(node.cargo_id, node.cargo_nome, setorId, node.superior_cargo_id, node.nivel)}
            title="Alterar superior hierárquico"
            className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <Edit3 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Ocupantes inline (Nível 4: Pessoas) */}
      {isOccupantsOpen && (
        <div className="mt-2 ml-4 pl-3 border-l-2 border-dashed border-gray-200 space-y-2 py-1">
          {node.colaboradores.length === 0 ? (
            <p className="text-xs text-gray-400 italic">Nenhum colaborador ativo neste cargo.</p>
          ) : (
            node.colaboradores.map((colab) => (
              <div key={colab.id} className="space-y-1.5">
                <div className="bg-gray-50/70 border border-gray-200/80 rounded-lg px-3 py-1.5 text-xs flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className="w-2 h-2 rounded-full bg-emerald-500" />
                    <span className="font-semibold text-gray-800">{colab.nome}</span>
                    {colab.cpf && <span className="text-gray-400">({colab.cpf})</span>}
                  </div>

                  <div className="flex items-center space-x-2">
                    {colab.has_aso && onDownloadAso ? (
                      <button
                        type="button"
                        onClick={() => onDownloadAso(colab.id, colab.nome)}
                        disabled={downloadingColabAsoId === colab.id}
                        className="inline-flex items-center gap-1 text-[10px] text-emerald-700 font-medium hover:underline cursor-pointer disabled:opacity-50"
                        title="Clique para baixar o ASO deste colaborador"
                      >
                        {downloadingColabAsoId === colab.id ? (
                          <>
                            <RefreshCw className="w-2.5 h-2.5 animate-spin" />
                            <span>Baixando...</span>
                          </>
                        ) : (
                          <>
                            <FileDown className="w-3 h-3 text-emerald-600" />
                            <span>Baixar ASO</span>
                          </>
                        )}
                      </button>
                    ) : null}
                    {colab.status_periodico === 'a_vencer' && (
                      <span className="text-[10px] text-amber-700 font-medium">A vencer</span>
                    )}
                    {colab.status_periodico === 'vencido' && (
                      <span className="text-[10px] text-rose-700 font-medium">Periódico vencido</span>
                    )}
                    {colab.status_periodico === 'agendado' && (
                      <span className="text-[10px] text-blue-700 font-medium">Agendado</span>
                    )}
                    {colab.status_periodico === 'sem_data' && (
                      <span className="text-[10px] text-gray-400">Sem data periódica</span>
                    )}

                    {onToggleEditColaborador && (
                      <button
                        type="button"
                        onClick={() => onToggleEditColaborador(colab.id)}
                        title="Editar cadastro do colaborador"
                        className={`p-1 rounded transition-colors ${
                          editingColaboradorId === colab.id
                            ? 'text-primary-700 bg-primary-100 font-bold'
                            : 'text-gray-400 hover:text-primary-600 hover:bg-gray-200/70'
                        }`}
                      >
                        <Edit3 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Accordion de Edição Inline */}
                {editingColaboradorId === colab.id && onToggleEditColaborador && onColaboradorSaved && (
                  <ColaboradorFichaAccordion
                    colaboradorId={colab.id}
                    cargoNome={node.cargo_nome}
                    setorNome={setorNome}
                    onClose={() => onToggleEditColaborador(colab.id)}
                    onSaved={onColaboradorSaved}
                  />
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* Subordinados recursivos */}
      {node.subordinados && node.subordinados.length > 0 && (
        <div className="space-y-2 mt-2">
          {node.subordinados.map((subNode) => (
            <CargoTreeItem
              key={subNode.cargo_id}
              node={subNode}
              setorId={setorId}
              setorNome={setorNome}
              depth={depth + 1}
              expandedOccupants={expandedOccupants}
              toggleOccupants={toggleOccupants}
              onOpenDetails={onOpenDetails}
              onDefinirSuperior={onDefinirSuperior}
              editingColaboradorId={editingColaboradorId}
              onToggleEditColaborador={onToggleEditColaborador}
              onColaboradorSaved={onColaboradorSaved}
              onDownloadAso={onDownloadAso}
              downloadingColabAsoId={downloadingColabAsoId}
            />
          ))}
        </div>
      )}
    </div>
  );
}
